import React, { useRef, useEffect, useCallback, useState } from 'react';
import {
  Point,
  IsoPoint,
  PlacedBlock,
  SocketConnection,
  ViewState,
  BackgroundSettings,
  ViewDisplaySettings,
  IsomPart,
  DrawingElement,
  ConnectionPoint,
  TextAnnotation,
  BlockSystemTool,
  BlockSystem,
  PipeType,
  ConnectionMarker,
  LabelTemplate,
  PlacedLabel,
} from '@/app/types/cad';
import {
  isoToScreen,
  screenToIso,
  snapToGrid,
  screenToWorld,
  worldToScreen,
  GRID_SIZE,
} from '@/app/utils/isometric';
import {
  transformPoint,
  getTransformedSockets,
  getTransformedLineEndpoints,
  snapToSocket,
  snapToAnyConnection,
  createDefaultTransform,
} from '@/app/utils/transform';
import { findBestSnap } from '@/app/utils/snapLogic';
import { resolveLabelData } from '@/app/utils/labelResolver';

// Type for unified dragging system
type DragItem = {
  position: IsoPoint;
  type: 'block' | 'text' | 'label';
};

interface BlockSystemCanvasProps {
  blocks: PlacedBlock[];
  connections: SocketConnection[];
  connectionMarkers?: ConnectionMarker[];
  selectedBlockIds: string[];
  selectedConnectionIds?: string[];
  selectedMarkerIds?: string[];
  textAnnotations?: TextAnnotation[];
  selectedTextIds?: string[];
  labelTemplates?: LabelTemplate[];
  placedLabels?: PlacedLabel[];
  selectedLabelIds?: string[];
  selectedLabelTemplateId?: string;
  systems?: BlockSystem[];
  pipeTypes?: PipeType[];
  projectName?: string;
  viewState: ViewState;
  backgroundSettings: BackgroundSettings;
  viewDisplaySettings?: ViewDisplaySettings;
  onViewStateChange: (viewState: ViewState) => void;
  onBlockAdd: (block: PlacedBlock) => void;
  onBlockUpdate: (id: string, updates: Partial<PlacedBlock>) => void;
  onBlockSelect: (ids: string[]) => void;
  onBlockContextMenu?: (blockId: string, x: number, y: number) => void;
  onConnectionSelect?: (ids: string[]) => void;
  onMarkerSelect?: (ids: string[]) => void;
  onTextSelect?: (ids: string[]) => void;
  onTextClick?: (position: IsoPoint) => void;
  onTextEdit?: (textId: string) => void;
  onTextUpdate?: (id: string, updates: Partial<TextAnnotation>) => void;
  onLabelSelect?: (ids: string[]) => void;
  onLabelAdd?: (label: PlacedLabel) => void;
  onLabelUpdate?: (id: string, updates: Partial<PlacedLabel>) => void;
  onLabelEdit?: (labelId: string) => void;
  onConnectionCreate: (connection: SocketConnection) => void;
  onBlockAddWithConnection?: (block: PlacedBlock, connection: SocketConnection) => void;
  onDimensionClick?: (blockId: string, dimensionId: string) => void;
  systemColors: Map<string, string>;
  showDimensions?: boolean;
  activeTool?: BlockSystemTool;
  pipeDrawStart?: IsoPoint | null;
  pipeDrawPreview?: IsoPoint | null;
  onPipeDrawStart?: (point: IsoPoint | null) => void;
  onPipeDrawPreview?: (point: IsoPoint | null) => void;
  onPipeDrawEnd?: (endPoint: IsoPoint) => void;
  snapEnabled?: boolean;
  filterConnectionsOnly?: boolean;
  onMouseMove?: (point: IsoPoint) => void;
  isPrintMode?: boolean;
}

export function BlockSystemCanvas({
  blocks,
  connections,
  connectionMarkers = [],
  selectedBlockIds,
  selectedConnectionIds = [],
  selectedMarkerIds = [],
  textAnnotations = [],
  selectedTextIds = [],
  labelTemplates = [],
  placedLabels = [],
  selectedLabelIds = [],
  selectedLabelTemplateId,
  systems = [],
  pipeTypes = [],
  projectName = '',
  viewState,
  backgroundSettings,
  viewDisplaySettings,
  onViewStateChange,
  onBlockAdd,
  onBlockUpdate,
  onBlockSelect,
  onBlockContextMenu,
  onConnectionSelect,
  onMarkerSelect,
  onTextSelect,
  onTextClick,
  onTextEdit,
  onTextUpdate,
  onLabelSelect,
  onLabelAdd,
  onLabelUpdate,
  onLabelEdit,
  onConnectionCreate,
  onBlockAddWithConnection,
  onDimensionClick,
  systemColors,
  showDimensions,
  activeTool = 'select',
  pipeDrawStart = null,
  pipeDrawPreview = null,
  onPipeDrawStart,
  onPipeDrawPreview,
  onPipeDrawEnd,
  snapEnabled = true,
  filterConnectionsOnly = false,
  onMouseMove,
  isPrintMode = false,
}: BlockSystemCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<{ x: number; y: number } | null>(null);
  const [hoverPoint, setHoverPoint] = useState<IsoPoint | null>(null);
  const [draggedBlockData, setDraggedBlockData] = useState<IsomPart | null>(null);
  const [dragPosition, setDragPosition] = useState<IsoPoint | null>(null);
  const [snapTarget, setSnapTarget] = useState<{
    type: 'socket' | 'line';
    blockId: string;
    socketIndex?: number;
    lineIndex?: number;
    isStart?: boolean;
  } | null>(null);
  
  // Cycle-through selection state
  const [lastClickPosition, setLastClickPosition] = useState<{ x: number; y: number } | null>(null);
  const [clickCycleIndex, setClickCycleIndex] = useState(0);

  // Default display settings
  const displaySettings = viewDisplaySettings || {
    lineWidthMultiplier: 1.0,
    connectionSizeMultiplier: 1.0,
    fontSizeMultiplier: 1.0,
  };

  // Dragging state for moving placed blocks, texts, and labels (UNIFIED)
  const [isDragging, setIsDragging] = useState(false);
  const [dragBlockId, setDragBlockId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ u: number; v: number } | null>(null);
  
  // HYBRID: Use refs for immediate access + increment counter to force re-render
  const dragStartPositionsRef = useRef<Map<string, DragItem>>(new Map());
  const dragStartMousePosRef = useRef<{ x: number; y: number } | null>(null);
  const [dragUpdateCounter, setDragUpdateCounter] = useState(0);

  // Selection box state
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<{ x: number; y: number } | null>(null);
  const [lastMousePos, setLastMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  
  // Hover state for text annotations
  const [hoveredTextId, setHoveredTextId] = useState<string | null>(null);
  
  // Label placement state
  const [labelPlacementStep, setLabelPlacementStep] = useState<'select-element' | 'position-label' | null>(null);
  const [labelTargetElement, setLabelTargetElement] = useState<{ type: 'connection' | 'block' | 'text-annotation'; id: string } | null>(null);
  const [labelPreviewPosition, setLabelPreviewPosition] = useState<IsoPoint | null>(null);

  // Get the current grid size based on background mode
  const gridSize = (() => {
    switch (backgroundSettings.mode) {
      case 'isometric':
        // Correct vertical spacing to match setting
        const angle = (backgroundSettings.isometricAngle * Math.PI) / 180;
        const sinA = Math.sin(angle);
        return Math.abs(sinA) > 0.001 
          ? backgroundSettings.isometricGridSize / sinA 
          : backgroundSettings.isometricGridSize;
      case 'cartesian':
        return backgroundSettings.cartesianGridSize;
      case 'dots':
        return backgroundSettings.dotsGridSize;
      default:
        return GRID_SIZE;
    }
  })();

  // Get all available sockets for snapping (excluding specific block)
  const getAllSockets = useCallback(
    (excludeBlockId?: string) => {
      const sockets: Array<{
        blockId: string;
        socketIndex: number;
        position: IsoPoint;
      }> = [];

      blocks.forEach((block) => {
        if (block.id === excludeBlockId) return;

        const transformedSockets = getTransformedSockets(
          block.blockData.elements,
          block.transform
        );

        transformedSockets.forEach((socket) => {
          sockets.push({
            blockId: block.id,
            socketIndex: socket.index,
            position: socket.position,
          });
        });
      });

      return sockets;
    },
    [blocks]
  );

  // Get all available line endpoints for snapping (excluding specific block)
  const getAllLineEndpoints = useCallback(
    (excludeBlockId?: string) => {
      const endpoints: Array<{
        blockId: string;
        lineIndex: number;
        isStart: boolean;
        position: IsoPoint;
      }> = [];

      blocks.forEach((block) => {
        if (block.id === excludeBlockId) return;

        const transformedLines = getTransformedLineEndpoints(
          block.blockData.elements,
          block.transform
        );

        transformedLines.forEach((line) => {
          // Add start endpoint
          endpoints.push({
            blockId: block.id,
            lineIndex: line.index,
            isStart: true,
            position: line.startPosition,
          });
          
          // Add end endpoint
          endpoints.push({
            blockId: block.id,
            lineIndex: line.index,
            isStart: false,
            position: line.endPosition,
          });
        });
      });

      return endpoints;
    },
    [blocks]
  );

  // Hit test: Check if screen position hits a block
  const hitTestBlock = useCallback(
    (screenPos: Point, tolerance = 10): PlacedBlock | null => {
      // Test blocks in reverse order (top to bottom)
      for (let i = blocks.length - 1; i >= 0; i--) {
        const block = blocks[i];
        
        // Test each element in the block
        for (const element of block.blockData.elements) {
          if (element.type === 'line') {
            const start = transformPoint(element.start, block.transform);
            const end = transformPoint(element.end, block.transform);
            const startWorld = isoToScreen(start, gridSize);
            const endWorld = isoToScreen(end, gridSize);
            const startScreen = worldToScreen(startWorld, viewState);
            const endScreen = worldToScreen(endWorld, viewState);

            // Distance from point to line segment
            const A = screenPos.x - startScreen.x;
            const B = screenPos.y - startScreen.y;
            const C = endScreen.x - startScreen.x;
            const D = endScreen.y - startScreen.y;

            const dot = A * C + B * D;
            const lenSq = C * C + D * D;
            let param = -1;
            if (lenSq !== 0) param = dot / lenSq;

            let xx, yy;
            if (param < 0) {
              xx = startScreen.x;
              yy = startScreen.y;
            } else if (param > 1) {
              xx = endScreen.x;
              yy = endScreen.y;
            } else {
              xx = startScreen.x + param * C;
              yy = startScreen.y + param * D;
            }

            const dx = screenPos.x - xx;
            const dy = screenPos.y - yy;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist <= tolerance) return block;
          } else if (element.type === 'connection') {
            const pos = transformPoint(element.position, block.transform);
            const worldPos = isoToScreen(pos, gridSize);
            const screenPosEl = worldToScreen(worldPos, viewState);
            const diameterInGridUnits = element.diameter / gridSize;
            const radiusInPixels = (diameterInGridUnits * gridSize * viewState.zoom * displaySettings.connectionSizeMultiplier) / 2;

            const dist = Math.sqrt(
              (screenPos.x - screenPosEl.x) ** 2 + 
              (screenPos.y - screenPosEl.y) ** 2
            );

            if (dist <= radiusInPixels + tolerance) return block;
          }
          // Add more element types as needed
        }
      }
      return null;
    },
    [blocks, viewState, gridSize]
  );

  // Check if text annotation is in selection box
  const isTextInBox = useCallback(
    (annotation: TextAnnotation, box: { minX: number; minY: number; maxX: number; maxY: number }): boolean => {
      const world = isoToScreen(annotation.position, gridSize);
      const screen = worldToScreen(world, viewState);

      // Check if the text position is inside the box
      return screen.x >= box.minX && screen.x <= box.maxX &&
             screen.y >= box.minY && screen.y <= box.maxY;
    },
    [viewState, gridSize]
  );

  // Check if block is in selection box
  const isBlockInBox = useCallback(
    (block: PlacedBlock, box: { minX: number; minY: number; maxX: number; maxY: number }): boolean => {
      // Check if any element point is inside the box
      for (const element of block.blockData.elements) {
        if (element.type === 'line') {
          const start = transformPoint(element.start, block.transform);
          const end = transformPoint(element.end, block.transform);
          const startWorld = isoToScreen(start, gridSize);
          const endWorld = isoToScreen(end, gridSize);
          const startScreen = worldToScreen(startWorld, viewState);
          const endScreen = worldToScreen(endWorld, viewState);

          const startInside = startScreen.x >= box.minX && startScreen.x <= box.maxX &&
                             startScreen.y >= box.minY && startScreen.y <= box.maxY;
          const endInside = endScreen.x >= box.minX && endScreen.x <= box.maxX &&
                           endScreen.y >= box.minY && endScreen.y <= box.maxY;

          if (startInside || endInside) return true;
        } else if (element.type === 'connection') {
          const pos = transformPoint(element.position, block.transform);
          const worldPos = isoToScreen(pos, gridSize);
          const screenPos = worldToScreen(worldPos, viewState);

          if (screenPos.x >= box.minX && screenPos.x <= box.maxX &&
              screenPos.y >= box.minY && screenPos.y <= box.maxY) {
            return true;
          }
        }
      }
      return false;
    },
    [viewState, gridSize]
  );

  // Check if connection is in selection box
  const isConnectionInBox = useCallback(
    (conn: SocketConnection, box: { minX: number; minY: number; maxX: number; maxY: number }): boolean => {
      // Check if connection has a valid position
      if (!conn.position) return false;

      // Get position in screen coordinates
      const world = isoToScreen(conn.position, gridSize);
      const screen = worldToScreen(world, viewState);

      // Check if the connection center point is inside the box
      return screen.x >= box.minX && screen.x <= box.maxX &&
             screen.y >= box.minY && screen.y <= box.maxY;
    },
    [viewState, gridSize]
  );

  // Draw the isometric grid - EXACTLY like ISO.BLOCK
  const drawIsometricGrid = useCallback(
    (ctx: CanvasRenderingContext2D, width: number, height: number) => {
      const settings = backgroundSettings;
      const gridSizeScaled = gridSize * viewState.zoom;

      // Adaptive rendering for low zoom levels to prevent "grey surface"
      // We keep the grid physically stable but increase the step (skip lines)
      // if they would be rendered too densely (less than 10px apart).
      const minScreenSpacing = 10; 
      const step = gridSizeScaled < minScreenSpacing ? Math.ceil(minScreenSpacing / gridSizeScaled) : 1;

      ctx.save();
      ctx.strokeStyle = settings.isometricLineColor;
      ctx.lineWidth = settings.isometricLineWidth;

      // Calculate canvas corners in world coordinates
      const topLeftWorld = screenToWorld({ x: 0, y: 0 }, viewState);
      const topRightWorld = screenToWorld({ x: width, y: 0 }, viewState);
      const bottomLeftWorld = screenToWorld({ x: 0, y: height }, viewState);
      const bottomRightWorld = screenToWorld({ x: width, y: height }, viewState);

      // Convert to iso coordinates
      const topLeftIso = screenToIso(topLeftWorld, gridSize);
      const topRightIso = screenToIso(topRightWorld, gridSize);
      const bottomLeftIso = screenToIso(bottomLeftWorld, gridSize);
      const bottomRightIso = screenToIso(bottomRightWorld, gridSize);

      // Calculate custom isoToScreen for this specific gridSize
      const angle = (settings.isometricAngle * Math.PI) / 180;
      const customIsoToScreen = (iso: IsoPoint): Point => {
        const x = iso.u * gridSize * Math.cos(angle) +
                  iso.v * gridSize * Math.cos(Math.PI - angle);
        const y = iso.u * gridSize * Math.sin(angle) +
                  iso.v * gridSize * Math.sin(Math.PI - angle);
        return { x, y };
      };

      const allU = [topLeftIso.u, topRightIso.u, bottomLeftIso.u, bottomRightIso.u];
      const allV = [topLeftIso.v, topRightIso.v, bottomLeftIso.v, bottomRightIso.v];

      // Align min/max to step
      const minU = Math.floor((Math.min(...allU) - 2) / step) * step;
      const maxU = Math.ceil((Math.max(...allU) + 2) / step) * step;
      const minV = Math.floor((Math.min(...allV) - 2) / step) * step;
      const maxV = Math.ceil((Math.max(...allV) + 2) / step) * step;

      // Draw vertical lines
      for (let u = minU; u <= maxU; u += step) {
        for (let v = minV; v <= maxV; v += step) {
          const worldPos = customIsoToScreen({ u, v });
          const screenPos = worldToScreen(worldPos, viewState);

          if (screenPos.x >= -50 && screenPos.x <= width + 50) {
            ctx.beginPath();
            ctx.moveTo(screenPos.x, -50);
            ctx.lineTo(screenPos.x, height + 50);
            ctx.stroke();
          }
        }
      }

      // Draw diagonal lines (constant v)
      for (let v = minV; v <= maxV; v += step) {
        const point1World = customIsoToScreen({ u: minU - 5 * step, v });
        const point2World = customIsoToScreen({ u: maxU + 5 * step, v });

        const point1Screen = worldToScreen(point1World, viewState);
        const point2Screen = worldToScreen(point2World, viewState);

        ctx.beginPath();
        ctx.moveTo(point1Screen.x, point1Screen.y);
        ctx.lineTo(point2Screen.x, point2Screen.y);
        ctx.stroke();
      }

      // Draw diagonal lines (constant u)
      for (let u = minU; u <= maxU; u += step) {
        const point1World = customIsoToScreen({ u, v: minV - 5 * step });
        const point2World = customIsoToScreen({ u, v: maxV + 5 * step });

        const point1Screen = worldToScreen(point1World, viewState);
        const point2Screen = worldToScreen(point2World, viewState);

        ctx.beginPath();
        ctx.moveTo(point1Screen.x, point1Screen.y);
        ctx.lineTo(point2Screen.x, point2Screen.y);
        ctx.stroke();
      }

      // Draw grid points
      if (settings.isometricPointSize > 0) {
        ctx.fillStyle = settings.isometricPointColor;
        for (let u = minU; u <= maxU; u += step) {
          for (let v = minV; v <= maxV; v += step) {
            const worldPos = customIsoToScreen({ u, v });
            const screenPos = worldToScreen(worldPos, viewState);

            if (
              screenPos.x >= -10 &&
              screenPos.x <= width + 10 &&
              screenPos.y >= -10 &&
              screenPos.y <= height + 10
            ) {
              ctx.beginPath();
              ctx.arc(screenPos.x, screenPos.y, settings.isometricPointSize, 0, Math.PI * 2);
              ctx.fill();
            }
          }
        }
      }

      ctx.restore();
    },
    [backgroundSettings, viewState, gridSize]
  );

  // Draw cartesian grid - EXACTLY like ISO.BLOCK
  const drawCartesianGrid = useCallback(
    (ctx: CanvasRenderingContext2D, width: number, height: number) => {
      const settings = backgroundSettings;
      const gridSizeScaled = settings.cartesianGridSize * viewState.zoom;

      if (gridSizeScaled < 2) return;

      const step = 1;

      ctx.save();

      // Minor lines
      if (settings.cartesianShowMinorLines && settings.cartesianMinorDivisions > 1) {
        const minorGridSize = gridSizeScaled / settings.cartesianMinorDivisions;
        ctx.strokeStyle = settings.cartesianMinorLineColor;
        ctx.lineWidth = settings.cartesianLineWidth * 0.5;

        const minX = Math.floor(-viewState.panX / minorGridSize) * minorGridSize;
        const maxX = Math.ceil((width - viewState.panX) / minorGridSize) * minorGridSize;
        const minY = Math.floor(-viewState.panY / minorGridSize) * minorGridSize;
        const maxY = Math.ceil((height - viewState.panY) / minorGridSize) * minorGridSize;

        for (let x = minX; x <= maxX; x += minorGridSize) {
          const screenX = x * viewState.zoom + viewState.panX;
          ctx.beginPath();
          ctx.moveTo(screenX, 0);
          ctx.lineTo(screenX, height);
          ctx.stroke();
        }

        for (let y = minY; y <= maxY; y += minorGridSize) {
          const screenY = y * viewState.zoom + viewState.panY;
          ctx.beginPath();
          ctx.moveTo(0, screenY);
          ctx.lineTo(width, screenY);
          ctx.stroke();
        }
      }

      // Major lines
      ctx.strokeStyle = settings.cartesianLineColor;
      ctx.lineWidth = settings.cartesianLineWidth;

      const minX = Math.floor(-viewState.panX / gridSizeScaled) * gridSizeScaled;
      const maxX = Math.ceil((width - viewState.panX) / gridSizeScaled) * gridSizeScaled;
      const minY = Math.floor(-viewState.panY / gridSizeScaled) * gridSizeScaled;
      const maxY = Math.ceil((height - viewState.panY) / gridSizeScaled) * gridSizeScaled;

      for (let x = minX; x <= maxX; x += gridSizeScaled) {
        const screenX = x * viewState.zoom + viewState.panX;
        ctx.beginPath();
        ctx.moveTo(screenX, 0);
        ctx.lineTo(screenX, height);
        ctx.stroke();
      }

      for (let y = minY; y <= maxY; y += gridSizeScaled) {
        const screenY = y * viewState.zoom + viewState.panY;
        ctx.beginPath();
        ctx.moveTo(0, screenY);
        ctx.lineTo(width, screenY);
        ctx.stroke();
      }

      ctx.restore();
    },
    [backgroundSettings, viewState]
  );

  // Draw dots grid - EXACTLY like ISO.BLOCK
  const drawDotsGrid = useCallback(
    (ctx: CanvasRenderingContext2D, width: number, height: number) => {
      const settings = backgroundSettings;
      const gridSizeScaled = settings.dotsGridSize * viewState.zoom;

      if (gridSizeScaled < 2) return;

      ctx.save();
      ctx.fillStyle = settings.dotsColor;

      const minX = Math.floor(-viewState.panX / gridSizeScaled) * gridSizeScaled;
      const maxX = Math.ceil((width - viewState.panX) / gridSizeScaled) * gridSizeScaled;
      const minY = Math.floor(-viewState.panY / gridSizeScaled) * gridSizeScaled;
      const maxY = Math.ceil((height - viewState.panY) / gridSizeScaled) * gridSizeScaled;

      for (let x = minX; x <= maxX; x += gridSizeScaled) {
        for (let y = minY; y <= maxY; y += gridSizeScaled) {
          const screenX = x * viewState.zoom + viewState.panX;
          const screenY = y * viewState.zoom + viewState.panY;

          if (
            screenX >= 0 &&
            screenX <= width &&
            screenY >= 0 &&
            screenY <= height
          ) {
            ctx.beginPath();
            ctx.arc(screenX, screenY, settings.dotsSize, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      ctx.restore();
    },
    [backgroundSettings, viewState]
  );

  // Draw background - EXACTLY like ISO.BLOCK
  const drawBackground = useCallback(
    (ctx: CanvasRenderingContext2D, width: number, height: number) => {
      if (!backgroundSettings.visible) return;

      switch (backgroundSettings.mode) {
        case 'isometric':
          drawIsometricGrid(ctx, width, height);
          break;
        case 'cartesian':
          drawCartesianGrid(ctx, width, height);
          break;
        case 'dots':
          drawDotsGrid(ctx, width, height);
          break;
      }
    },
    [backgroundSettings, drawIsometricGrid, drawCartesianGrid, drawDotsGrid]
  );

  // Draw single element (from block)
  const drawElement = useCallback(
    (ctx: CanvasRenderingContext2D, element: DrawingElement, transform: any, blockAttributes?: any[], allElements?: DrawingElement[]) => {
      if (element.type === 'line') {
        const start = transformPoint(element.start, transform);
        const end = transformPoint(element.end, transform);
        const startWorld = isoToScreen(start, gridSize);
        const endWorld = isoToScreen(end, gridSize);
        let startScreen = worldToScreen(startWorld, viewState);
        let endScreen = worldToScreen(endWorld, viewState);

        // Offset line endpoints if they connect to sockets
        if (allElements) {
          const connectionElements = allElements.filter(e => e.type === 'connection');
          for (const socketElement of connectionElements) {
            if (socketElement.type !== 'connection') continue;
            
            const connPos = transformPoint(socketElement.position, transform);
            const connWorld = isoToScreen(connPos, gridSize);
            const connScreenPos = worldToScreen(connWorld, viewState);
            
            // Calculate socket radius (with size multiplier from display settings)
            const diameterInGridUnits = socketElement.diameter / gridSize;
            const radiusInPixels = (diameterInGridUnits * gridSize * viewState.zoom * displaySettings.connectionSizeMultiplier) / 2;

            // Check if line start is at connection point
            const distToStart = Math.sqrt(
              Math.pow(connScreenPos.x - startScreen.x, 2) + 
              Math.pow(connScreenPos.y - startScreen.y, 2)
            );
            
            if (distToStart < radiusInPixels + 2) {
              // Calculate direction from connection to line end
              const dx = endScreen.x - connScreenPos.x;
              const dy = endScreen.y - connScreenPos.y;
              const length = Math.sqrt(dx * dx + dy * dy);
              if (length > 0) {
                // Move start point to edge of connection circle
                startScreen = {
                  x: connScreenPos.x + (dx / length) * radiusInPixels,
                  y: connScreenPos.y + (dy / length) * radiusInPixels
                };
              }
            }

            // Check if line end is at connection point
            const distToEnd = Math.sqrt(
              Math.pow(connScreenPos.x - endScreen.x, 2) + 
              Math.pow(connScreenPos.y - endScreen.y, 2)
            );
            
            if (distToEnd < radiusInPixels + 2) {
              // Calculate direction from connection to line start
              const dx = startScreen.x - connScreenPos.x;
              const dy = startScreen.y - connScreenPos.y;
              const length = Math.sqrt(dx * dx + dy * dy);
              if (length > 0) {
                // Move end point to edge of connection circle
                endScreen = {
                  x: connScreenPos.x + (dx / length) * radiusInPixels,
                  y: connScreenPos.y + (dy / length) * radiusInPixels
                };
              }
            }
          }
        }

        ctx.strokeStyle = element.color;
        ctx.lineWidth = element.width * viewState.zoom * displaySettings.lineWidthMultiplier;
        ctx.lineCap = 'round';
        
        // Set line dash pattern based on lineStyle
        const lineStyle = element.lineStyle || 'solid';
        switch (lineStyle) {
          case 'solid':
            ctx.setLineDash([]);
            break;
          case 'dashed':
            ctx.setLineDash([10, 5]);
            break;
          case 'dotted':
            ctx.setLineDash([2, 3]);
            break;
          case 'dashdot':
            ctx.setLineDash([10, 3, 2, 3]);
            break;
          default:
            ctx.setLineDash([]);
        }
        
        ctx.beginPath();
        ctx.moveTo(startScreen.x, startScreen.y);
        ctx.lineTo(endScreen.x, endScreen.y);
        ctx.stroke();
        
        // Reset line dash for other elements
        ctx.setLineDash([]);
      } else if (element.type === 'text') {
        // Render text element
        const pos = transformPoint(element.position, transform);
        const world = isoToScreen(pos, gridSize);
        const screen = worldToScreen(world, viewState);

        // Resolve attribute placeholders in text
        let displayText = element.text;
        const placeholderRegex = /\{([^}]+)\}/g;
        const matches = element.text.matchAll(placeholderRegex);
        
        for (const match of matches) {
          const placeholder = match[0]; // e.g., "{DN}"
          const attrName = match[1]; // e.g., "DN"
          
          // Find matching attribute (case-insensitive)
          const attr = blockAttributes?.find(a => a.name.toLowerCase() === attrName.toLowerCase());
          
          if (attr) {
            // Replace with actual value
            const value = attr.value?.toString() || '';
            displayText = displayText.replace(placeholder, value);
          }
          // If attribute not found, leave placeholder as-is
        }

        ctx.save();
        ctx.fillStyle = element.color;
        ctx.font = `${element.fontSize * viewState.zoom * displaySettings.fontSizeMultiplier}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        if (element.rotation !== 0) {
          ctx.translate(screen.x, screen.y);
          ctx.rotate(element.rotation);
          ctx.fillText(displayText, 0, 0);
        } else {
          ctx.fillText(displayText, screen.x, screen.y);
        }

        ctx.restore();
      } else if (element.type === 'shape') {
        const transformedPoints = element.points.map((p) => transformPoint(p, transform));
        const screenPoints = transformedPoints.map((p) => {
          const world = isoToScreen(p, gridSize);
          return worldToScreen(world, viewState);
        });

        ctx.beginPath();
        screenPoints.forEach((p, i) => {
          if (i === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        });
        ctx.closePath();

        if (element.filled) {
          ctx.fillStyle = element.fillColor;
          ctx.fill();
        }

        ctx.strokeStyle = element.strokeColor;
        ctx.lineWidth = element.strokeWidth * viewState.zoom * displaySettings.lineWidthMultiplier;
        ctx.stroke();
      } else if (element.type === 'circle') {
        const center = transformPoint(element.center, transform);
        const centerWorld = isoToScreen(center, gridSize);
        const centerScreen = worldToScreen(centerWorld, viewState);
        const radiusScreen = element.radius * gridSize * viewState.zoom;

        ctx.strokeStyle = element.color;
        ctx.lineWidth = element.width * viewState.zoom * displaySettings.lineWidthMultiplier;
        
        // Set line dash pattern based on lineStyle
        const lineStyle = element.lineStyle || 'solid';
        switch (lineStyle) {
          case 'solid':
            ctx.setLineDash([]);
            break;
          case 'dashed':
            ctx.setLineDash([10, 5]);
            break;
          case 'dotted':
            ctx.setLineDash([2, 3]);
            break;
          case 'dashdot':
            ctx.setLineDash([10, 3, 2, 3]);
            break;
          default:
            ctx.setLineDash([]);
        }
        
        ctx.beginPath();
        ctx.arc(centerScreen.x, centerScreen.y, radiusScreen, 0, Math.PI * 2);

        if (element.filled) {
          ctx.fillStyle = element.fillColor;
          ctx.fill();
        }

        ctx.stroke();
        
        // Reset line dash for other elements
        ctx.setLineDash([]);
      } else if (element.type === 'connection') {
        const pos = transformPoint(element.position, transform);
        const world = isoToScreen(pos, gridSize);
        const screen = worldToScreen(world, viewState);

        // Convert diameter from mm to screen pixels - EXACTLY like ISO.BLOCK
        const diameterInGridUnits = element.diameter / gridSize;
        const radiusInPixels = (diameterInGridUnits * gridSize * viewState.zoom * displaySettings.connectionSizeMultiplier) / 2;

        ctx.save();

        // Draw based on fillType
        if (element.fillType === 'filled') {
          // Draw filled circle
          ctx.fillStyle = element.color;
          ctx.beginPath();
          ctx.arc(screen.x, screen.y, radiusInPixels, 0, Math.PI * 2);
          ctx.fill();
        } else {
          // Draw ring (hollow circle)
          ctx.strokeStyle = element.color;
          ctx.lineWidth = element.strokeWidth * viewState.zoom * displaySettings.lineWidthMultiplier;
          ctx.beginPath();
          ctx.arc(screen.x, screen.y, radiusInPixels, 0, Math.PI * 2);
          ctx.stroke();
        }

        // Draw label if exists
        if (element.label) {
          ctx.font = `bold ${13 * viewState.zoom * displaySettings.fontSizeMultiplier}px Arial`;
          ctx.fillStyle = element.color;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          
          // Add background for better readability
          const textMetrics = ctx.measureText(element.label);
          const textWidth = textMetrics.width;
          const textHeight = 13 * viewState.zoom;
          const padding = 3 * viewState.zoom;
          
          const labelY = screen.y + radiusInPixels + 5 * viewState.zoom;
          
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.fillRect(
            screen.x - textWidth / 2 - padding,
            labelY - padding,
            textWidth + padding * 2,
            textHeight + padding * 2
          );
          
          ctx.fillStyle = element.color;
          ctx.fillText(element.label, screen.x, labelY);
        }

        ctx.restore();
      } else if (element.type === 'dimension' && showDimensions) {
        // Draw dimension - transformed
        const start = transformPoint(element.start, transform);
        const end = transformPoint(element.end, transform);
        const startWorld = isoToScreen(start, gridSize);
        const endWorld = isoToScreen(end, gridSize);
        const startScreen = worldToScreen(startWorld, viewState);
        const endScreen = worldToScreen(endWorld, viewState);

        // Calculate perpendicular offset
        const dx = endScreen.x - startScreen.x;
        const dy = endScreen.y - startScreen.y;
        const length = Math.sqrt(dx * dx + dy * dy);
        
        if (length === 0) return;

        // Perpendicular vector (normalized)
        const perpX = -dy / length;
        const perpY = dx / length;

        // Offset distance in screen coordinates
        const offsetDistance = element.offset * gridSize * viewState.zoom;

        // Calculate dimension line endpoints
        const dimStartX = startScreen.x + perpX * offsetDistance;
        const dimStartY = startScreen.y + perpY * offsetDistance;
        const dimEndX = endScreen.x + perpX * offsetDistance;
        const dimEndY = endScreen.y + perpY * offsetDistance;

        ctx.save();

        ctx.strokeStyle = element.color;
        ctx.lineWidth = (element.lineWidth ?? 1) * viewState.zoom * displaySettings.lineWidthMultiplier;
        ctx.setLineDash([]);

        // Only draw lines if showLine is true
        if (element.showLine) {
          // Draw extension lines from measured points to dimension line
          ctx.beginPath();
          ctx.moveTo(startScreen.x, startScreen.y);
          ctx.lineTo(dimStartX + perpX * 5 * viewState.zoom, dimStartY + perpY * 5 * viewState.zoom);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(endScreen.x, endScreen.y);
          ctx.lineTo(dimEndX + perpX * 5 * viewState.zoom, dimEndY + perpY * 5 * viewState.zoom);
          ctx.stroke();

          // Draw dimension line
          ctx.beginPath();
          ctx.moveTo(dimStartX, dimStartY);
          ctx.lineTo(dimEndX, dimEndY);
          ctx.stroke();
        }

        // Only draw arrows if showArrows is true
        if (element.showArrows) {
          const arrowSize = (element.arrowSize ?? 8) * viewState.zoom;
          const arrowAngle = Math.atan2(dimEndY - dimStartY, dimEndX - dimStartX);
          const arrowFilled = element.arrowFilled ?? false;
          const arrowPosition = element.arrowPosition ?? 'inside';

          let arrow1X = dimStartX;
          let arrow1Y = dimStartY;
          let arrow2X = dimEndX;
          let arrow2Y = dimEndY;

          // Draw arrow at start
          if (arrowFilled) {
            ctx.fillStyle = element.color;
            ctx.beginPath();
            ctx.moveTo(arrow1X, arrow1Y);
            if (arrowPosition === 'inside') {
              ctx.lineTo(
                arrow1X + arrowSize * Math.cos(arrowAngle - Math.PI / 6),
                arrow1Y + arrowSize * Math.sin(arrowAngle - Math.PI / 6)
              );
              ctx.lineTo(
                arrow1X + arrowSize * Math.cos(arrowAngle + Math.PI / 6),
                arrow1Y + arrowSize * Math.sin(arrowAngle + Math.PI / 6)
              );
            } else {
              ctx.lineTo(
                arrow1X - arrowSize * Math.cos(arrowAngle - Math.PI / 6),
                arrow1Y - arrowSize * Math.sin(arrowAngle - Math.PI / 6)
              );
              ctx.lineTo(
                arrow1X - arrowSize * Math.cos(arrowAngle + Math.PI / 6),
                arrow1Y - arrowSize * Math.sin(arrowAngle + Math.PI / 6)
              );
            }
            ctx.closePath();
            ctx.fill();
          } else {
            ctx.beginPath();
            ctx.moveTo(arrow1X, arrow1Y);
            if (arrowPosition === 'inside') {
              ctx.lineTo(
                arrow1X + arrowSize * Math.cos(arrowAngle - Math.PI / 6),
                arrow1Y + arrowSize * Math.sin(arrowAngle - Math.PI / 6)
              );
              ctx.moveTo(arrow1X, arrow1Y);
              ctx.lineTo(
                arrow1X + arrowSize * Math.cos(arrowAngle + Math.PI / 6),
                arrow1Y + arrowSize * Math.sin(arrowAngle + Math.PI / 6)
              );
            } else {
              ctx.lineTo(
                arrow1X - arrowSize * Math.cos(arrowAngle - Math.PI / 6),
                arrow1Y - arrowSize * Math.sin(arrowAngle - Math.PI / 6)
              );
              ctx.moveTo(arrow1X, arrow1Y);
              ctx.lineTo(
                arrow1X - arrowSize * Math.cos(arrowAngle + Math.PI / 6),
                arrow1Y - arrowSize * Math.sin(arrowAngle + Math.PI / 6)
              );
            }
            ctx.stroke();
          }

          // Draw arrow at end
          if (arrowFilled) {
            ctx.fillStyle = element.color;
            ctx.beginPath();
            ctx.moveTo(arrow2X, arrow2Y);
            if (arrowPosition === 'inside') {
              ctx.lineTo(
                arrow2X - arrowSize * Math.cos(arrowAngle - Math.PI / 6),
                arrow2Y - arrowSize * Math.sin(arrowAngle - Math.PI / 6)
              );
              ctx.lineTo(
                arrow2X - arrowSize * Math.cos(arrowAngle + Math.PI / 6),
                arrow2Y - arrowSize * Math.sin(arrowAngle + Math.PI / 6)
              );
            } else {
              ctx.lineTo(
                arrow2X + arrowSize * Math.cos(arrowAngle - Math.PI / 6),
                arrow2Y + arrowSize * Math.sin(arrowAngle - Math.PI / 6)
              );
              ctx.lineTo(
                arrow2X + arrowSize * Math.cos(arrowAngle + Math.PI / 6),
                arrow2Y + arrowSize * Math.sin(arrowAngle + Math.PI / 6)
              );
            }
            ctx.closePath();
            ctx.fill();
          } else {
            ctx.beginPath();
            ctx.moveTo(arrow2X, arrow2Y);
            if (arrowPosition === 'inside') {
              ctx.lineTo(
                arrow2X - arrowSize * Math.cos(arrowAngle - Math.PI / 6),
                arrow2Y - arrowSize * Math.sin(arrowAngle - Math.PI / 6)
              );
              ctx.moveTo(arrow2X, arrow2Y);
              ctx.lineTo(
                arrow2X - arrowSize * Math.cos(arrowAngle + Math.PI / 6),
                arrow2Y - arrowSize * Math.sin(arrowAngle + Math.PI / 6)
              );
            } else {
              ctx.lineTo(
                arrow2X + arrowSize * Math.cos(arrowAngle - Math.PI / 6),
                arrow2Y + arrowSize * Math.sin(arrowAngle - Math.PI / 6)
              );
              ctx.moveTo(arrow2X, arrow2Y);
              ctx.lineTo(
                arrow2X + arrowSize * Math.cos(arrowAngle + Math.PI / 6),
                arrow2Y + arrowSize * Math.sin(arrowAngle + Math.PI / 6)
              );
            }
            ctx.stroke();
          }
        }

        // Draw dimension text with unit
        const midX = (dimStartX + dimEndX) / 2;
        const midY = (dimStartY + dimEndY) / 2;

        // Use actualValue if overridden, otherwise use calculated value
        const valueToDisplay = element.actualValue || element.value;
        const isOverridden = element.actualValue && element.actualValue !== element.value;
        
        // Format value with specified decimal places
        const numericValue = parseFloat(valueToDisplay);
        const decimalPlaces = element.decimalPlaces ?? 1;
        const formattedValue = isNaN(numericValue) ? valueToDisplay : numericValue.toFixed(decimalPlaces);
        const displayText = (element.showUnit && element.unit) ? `${formattedValue} ${element.unit}` : formattedValue;

        // Calculate angle of dimension line
        const lineAngle = Math.atan2(dimEndY - dimStartY, dimEndX - dimStartX);
        
        // Adjust angle to keep text readable (never upside down)
        let textAngle = lineAngle;
        if (textAngle > Math.PI / 2 || textAngle < -Math.PI / 2) {
          textAngle = textAngle + Math.PI;
        }

        ctx.font = `${element.textSize * viewState.zoom * displaySettings.fontSizeMultiplier}px Arial`;
        ctx.fillStyle = element.color;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Measure text for background
        const textMetrics = ctx.measureText(displayText);
        const textWidth = textMetrics.width;
        const textHeight = element.textSize * viewState.zoom * displaySettings.fontSizeMultiplier;
        const padding = 4 * viewState.zoom;

        // Save context and translate/rotate for text
        ctx.save();
        ctx.translate(midX, midY);
        ctx.rotate(textAngle);

        // Background for text (rotated) - highlight if overridden
        if (isOverridden) {
          ctx.fillStyle = 'rgba(255, 200, 0, 0.95)'; // Orange background for overridden values
        } else {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        }
        ctx.fillRect(
          -textWidth / 2 - padding,
          -textHeight / 2 - padding,
          textWidth + padding * 2,
          textHeight + padding * 2
        );

        // Draw text (rotated)
        ctx.fillStyle = element.color;
        ctx.fillText(displayText, 0, 0);
        
        // Add indicator if overridden
        if (isOverridden) {
          ctx.fillStyle = '#ff8800';
          ctx.font = `bold ${element.textSize * 0.6 * viewState.zoom * displaySettings.fontSizeMultiplier}px Arial`;
          ctx.fillText('✎', textWidth / 2 + padding * 2, -textHeight / 3);
        }
        
        ctx.restore();

        ctx.restore();
      }
      // Add more element types as needed (arc, spline, etc.)
    },
    [viewState, gridSize, showDimensions]
  );

  // Draw selection box
  const drawSelectionBox = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      if (!selectionStart || !selectionEnd || !isSelecting) return;

      const minX = Math.min(selectionStart.x, selectionEnd.x);
      const minY = Math.min(selectionStart.y, selectionEnd.y);
      const maxX = Math.max(selectionStart.x, selectionEnd.x);
      const maxY = Math.max(selectionStart.y, selectionEnd.y);

      ctx.save();
      
      // Fill
      ctx.fillStyle = 'rgba(59, 130, 246, 0.1)';
      ctx.fillRect(minX, minY, maxX - minX, maxY - minY);
      
      // Border
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.5)';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(minX, minY, maxX - minX, maxY - minY);
      
      ctx.restore();
    },
    [selectionStart, selectionEnd, isSelecting]
  );

  // Draw placed block
  const drawBlock = useCallback(
    (ctx: CanvasRenderingContext2D, block: PlacedBlock, isSelected: boolean) => {
      ctx.save();

      // Calculate effective display settings (block overrides take precedence)
      const effectiveDisplaySettings = {
        lineWidthMultiplier: block.viewDisplayOverrides?.lineWidthMultiplier ?? displaySettings.lineWidthMultiplier,
        connectionSizeMultiplier: block.viewDisplayOverrides?.connectionSizeMultiplier ?? displaySettings.connectionSizeMultiplier,
        fontSizeMultiplier: block.viewDisplayOverrides?.fontSizeMultiplier ?? displaySettings.fontSizeMultiplier,
      };

      // Get block attributes for text replacement
      const blockAttributes = block.blockData.attributes || [];

      // Get system color if block belongs to a system
      const systemColor = block.systemId ? systemColors.get(block.systemId) : undefined;

      // Temporarily override displaySettings for this block
      const originalDisplaySettings = { ...displaySettings };
      Object.assign(displaySettings, effectiveDisplaySettings);
      
      // Draw all elements - override colors if part of system or has color override
      block.blockData.elements.forEach((element, elementIndex) => {
        // Sockets (connection elements) werden immer gerendert
        // Die Verbindungen zwischen den Sockets werden separat in drawConnections() gerendert
        // wenn filterConnectionsOnly aktiviert ist
        
        // Create a copy of the element with potentially overridden color
        let elementToDraw = element;
        
        // Priority: 1. Block color override, 2. System color, 3. Original color
        const effectiveColor = block.colorOverride || systemColor;
        
        if (effectiveColor && (element.type === 'line' || element.type === 'connection' || element.type === 'circle')) {
          elementToDraw = { ...element, color: effectiveColor };
        } else if (effectiveColor && element.type === 'shape') {
          elementToDraw = { ...element, strokeColor: effectiveColor };
        }
        
        drawElement(ctx, elementToDraw, block.transform, blockAttributes, block.blockData.elements);
      });
      
      // Restore original displaySettings
      Object.assign(displaySettings, originalDisplaySettings);

      // Draw selection outline ONLY if selected (no dashed box for systems)
      if (isSelected) {
        // Get bounding box
        const points: IsoPoint[] = [];
        block.blockData.elements.forEach((el) => {
          if (el.type === 'line') {
            points.push(transformPoint(el.start, block.transform));
            points.push(transformPoint(el.end, block.transform));
          } else if (el.type === 'shape') {
            el.points.forEach((p) => points.push(transformPoint(p, block.transform)));
          } else if (el.type === 'connection') {
            points.push(transformPoint(el.position, block.transform));
          }
        });

        if (points.length > 0) {
          const minU = Math.min(...points.map((p) => p.u));
          const maxU = Math.max(...points.map((p) => p.u));
          const minV = Math.min(...points.map((p) => p.v));
          const maxV = Math.max(...points.map((p) => p.v));

          const padding = 0.5;
          const corners = [
            { u: minU - padding, v: minV - padding },
            { u: maxU + padding, v: minV - padding },
            { u: maxU + padding, v: maxV + padding },
            { u: minU - padding, v: maxV + padding },
          ];

          const screenCorners = corners.map((p) => {
            const world = isoToScreen(p, gridSize);
            return worldToScreen(world, viewState);
          });

          ctx.strokeStyle = '#0066ff';
          ctx.lineWidth = 3 * viewState.zoom;
          ctx.setLineDash([]);

          ctx.beginPath();
          screenCorners.forEach((p, i) => {
            if (i === 0) ctx.moveTo(p.x, p.y);
            else ctx.lineTo(p.x, p.y);
          });
          ctx.closePath();
          ctx.stroke();

          ctx.setLineDash([]);
        }
      }

      ctx.restore();
    },
    [viewState, gridSize, systemColors, drawElement]
  );

  // Draw drag preview
  const drawDragPreview = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      if (!draggedBlockData || !dragPosition) return;

      ctx.save();
      ctx.globalAlpha = 0.6;

      const transform = createDefaultTransform(dragPosition);

      draggedBlockData.elements.forEach((element) => {
        drawElement(ctx, element, transform);
      });

      // Highlight if snapped
      if (snapTarget) {
        const world = isoToScreen(dragPosition, gridSize);
        const screen = worldToScreen(world, viewState);

        ctx.strokeStyle = '#00ff00';
        ctx.lineWidth = 3 * viewState.zoom;
        ctx.beginPath();
        ctx.arc(screen.x, screen.y, 15 * viewState.zoom, 0, Math.PI * 2);
        ctx.stroke();
      }

      ctx.restore();
    },
    [draggedBlockData, dragPosition, snapTarget, viewState, gridSize, drawElement]
  );

  // Draw hover point
  const drawHoverPoint = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      if (!hoverPoint) return;

      const worldPos = isoToScreen(hoverPoint, gridSize);
      const screenPos = worldToScreen(worldPos, viewState);

      ctx.save();
      ctx.fillStyle = 'rgba(0, 102, 255, 0.3)';
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y, 6, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = '#0066ff';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
    },
    [hoverPoint, viewState, gridSize]
  );

  // Draw available snap points (sockets and line endpoints)
  const drawSnapPoints = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      // Show when dragging from library OR when moving existing blocks
      if (!draggedBlockData && !isDragging) return;

      ctx.save();
      
      // Draw all available sockets
      const allSockets = getAllSockets();
      allSockets.forEach((socket) => {
        const worldPos = isoToScreen(socket.position, gridSize);
        const screenPos = worldToScreen(worldPos, viewState);

        ctx.fillStyle = 'rgba(0, 255, 0, 0.2)';
        ctx.strokeStyle = '#00ff00';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y, 8 * viewState.zoom, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      });

      // Draw all available line endpoints
      const allEndpoints = getAllLineEndpoints();
      allEndpoints.forEach((endpoint) => {
        const worldPos = isoToScreen(endpoint.position, gridSize);
        const screenPos = worldToScreen(worldPos, viewState);

        ctx.fillStyle = 'rgba(255, 165, 0, 0.2)';
        ctx.strokeStyle = '#ffa500';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y, 6 * viewState.zoom, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      });

      ctx.restore();
    },
    [draggedBlockData, isDragging, getAllSockets, getAllLineEndpoints, viewState, gridSize]
  );

  // Draw connections between blocks (NEUE IMPLEMENTIERUNG)
  const drawConnections = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      // Nur rendern wenn der Verbindungsanzeige-Modus aktiviert ist
      if (!filterConnectionsOnly) {
        return;
      }
      
      connections.forEach((conn) => {
        // Neue Connection-Struktur verwenden
        if (!conn.position || !conn.connectedSockets || conn.connectedSockets.length < 2) {
          // Skip invalid connections
          return;
        }
        
        // Check if this connection is selected
        const isSelected = selectedConnectionIds.includes(conn.id);
        
        // Get position in screen coordinates
        const world = isoToScreen(conn.position, gridSize);
        const screen = worldToScreen(world, viewState);
        
        // Calculate radius based on diameter (with display settings)
        let radius = 7 * viewState.zoom; // Default radius
        if (conn.diameter) {
          const diameterInGridUnits = conn.diameter / gridSize;
          radius = (diameterInGridUnits * gridSize * viewState.zoom * displaySettings.connectionSizeMultiplier) / 2;
        }
        
        // Get color (system color or default)
        const color = conn.color || '#0066ff';
        
        ctx.save();
        
        // Draw single clean circle - filled with transparency
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.6;
        ctx.beginPath();
        ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
        ctx.fill();
        
        // Draw single border (orange if selected, otherwise system color)
        ctx.strokeStyle = isSelected ? '#ff6600' : color;
        ctx.lineWidth = isSelected ? 3 * viewState.zoom : 2 * viewState.zoom;
        ctx.globalAlpha = 1.0;
        ctx.beginPath();
        ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
        ctx.stroke();
        
        // Draw DN value with better visibility
        if (conn.dn && viewState.zoom > 0.5) {
          const fontSize = Math.max(11, 14 * viewState.zoom);
          ctx.font = `bold ${fontSize}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          // Text shadow for better readability
          ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
          ctx.shadowBlur = 4;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          ctx.fillStyle = '#ffffff';
          ctx.globalAlpha = 1.0;
          ctx.fillText(`DN${conn.dn}`, screen.x, screen.y);
          
          // Reset shadow
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
        }
        
        ctx.restore();
      });
    },
    [connections, blocks, systemColors, viewState, gridSize, displaySettings, selectedConnectionIds, filterConnectionsOnly]
  );

  // Draw connection markers (visual indicators)
  const drawConnectionMarkers = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      // NICHT rendern - die Connections werden bereits von drawConnections() gerendert
      // Diese Funktion ist nicht mehr notwendig
      return;
    },
    []
  );

  // Draw pipe preview during drawing
  const drawPipePreview = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      if (!pipeDrawStart || !pipeDrawPreview) return;

      const startWorld = isoToScreen(pipeDrawStart, gridSize);
      const endWorld = isoToScreen(pipeDrawPreview, gridSize);
      const startScreen = worldToScreen(startWorld, viewState);
      const endScreen = worldToScreen(endWorld, viewState);

      ctx.save();

      // Draw preview line
      ctx.strokeStyle = '#0066ff';
      ctx.lineWidth = 3 * viewState.zoom;
      ctx.setLineDash([10, 5]);
      ctx.beginPath();
      ctx.moveTo(startScreen.x, startScreen.y);
      ctx.lineTo(endScreen.x, endScreen.y);
      ctx.stroke();

      // Draw start point
      ctx.setLineDash([]);
      ctx.fillStyle = '#00ff00';
      ctx.beginPath();
      ctx.arc(startScreen.x, startScreen.y, 8 * viewState.zoom, 0, Math.PI * 2);
      ctx.fill();

      // Draw end point (current mouse position)
      ctx.fillStyle = '#ff9900';
      ctx.beginPath();
      ctx.arc(endScreen.x, endScreen.y, 8 * viewState.zoom, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    },
    [pipeDrawStart, pipeDrawPreview, viewState, gridSize]
  );

  // Check if a point is inside a text annotation
  const isPointInTextAnnotation = useCallback(
    (annotation: TextAnnotation, screenPoint: Point): boolean => {
      const world = isoToScreen(annotation.position, gridSize);
      const screen = worldToScreen(world, viewState);

      // Resolve placeholders to get actual display text
      let displayText = annotation.text;
      displayText = displayText.replace(/{project\.name}/g, projectName);
      
      const systemRegex = /{system:([^.]+)\.([^}]+)}/g;
      displayText = displayText.replace(systemRegex, (match, systemId, property) => {
        const system = systems.find(s => s.id === systemId);
        if (system) {
          if (property === 'name') return system.name;
          if (property.startsWith('attribute.')) {
            const attrName = property.substring(10);
            const attr = system.attributes?.find(a => a.name === attrName);
            return attr ? String(attr.value) : match;
          }
        }
        return match;
      });
      
      const pipeTypeRegex = /{pipetype:([^.]+)\.([^}]+)}/g;
      displayText = displayText.replace(pipeTypeRegex, (match, pipeTypeId, property) => {
        const pipeType = pipeTypes.find(p => p.id === pipeTypeId);
        if (pipeType) {
          if (property === 'name') return pipeType.name;
          if (property === 'dn') return `DN ${pipeType.dn}`;
        }
        return match;
      });

      // Create temporary canvas to measure text
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      if (!tempCtx) return false;

      tempCtx.font = `${annotation.fontSize * viewState.zoom * displaySettings.fontSizeMultiplier}px Arial`;
      const textMetrics = tempCtx.measureText(displayText);
      const textWidth = textMetrics.width;
      const textHeight = annotation.fontSize * viewState.zoom * displaySettings.fontSizeMultiplier;

      // Transform screen point to local coordinate system (inverse transform)
      // Step 1: Translate to origin (subtract text position)
      const localX = screenPoint.x - screen.x;
      const localY = screenPoint.y - screen.y;
      
      // Step 2: Apply inverse rotation
      const cosAngle = Math.cos(-annotation.rotation);
      const sinAngle = Math.sin(-annotation.rotation);
      const rotatedX = localX * cosAngle - localY * sinAngle;
      const rotatedY = localX * sinAngle + localY * cosAngle;

      // Calculate bounding box in local coordinate system (centered at 0,0)
      const padding = Math.max(10, annotation.fontSize * 0.5) * viewState.zoom;
      
      let boxX = -padding;
      let boxWidth = textWidth + padding * 2;
      
      if (annotation.alignment === 'center') {
        boxX = -textWidth / 2 - padding;
      } else if (annotation.alignment === 'right') {
        boxX = -textWidth - padding;
      }
      
      const boxY = -textHeight / 2 - padding;
      const boxHeight = textHeight + padding * 2;

      // Hit test in local coordinate system
      return (
        rotatedX >= boxX &&
        rotatedX <= boxX + boxWidth &&
        rotatedY >= boxY &&
        rotatedY <= boxY + boxHeight
      );
    },
    [viewState, gridSize, displaySettings, systems, pipeTypes, projectName]
  );

  // Draw text annotation
  const drawTextAnnotation = useCallback(
    (ctx: CanvasRenderingContext2D, annotation: TextAnnotation, isSelected: boolean, isHovered: boolean = false) => {
      const world = isoToScreen(annotation.position, gridSize);
      const screen = worldToScreen(world, viewState);

      // Resolve placeholders
      let displayText = annotation.text;
      
      // Project name placeholder
      displayText = displayText.replace(/{project\.name}/g, projectName);
      
      // System placeholders
      const systemRegex = /{system:([^.]+)\.([^}]+)}/g;
      displayText = displayText.replace(systemRegex, (match, systemId, property) => {
        const system = systems.find(s => s.id === systemId);
        if (system) {
          if (property === 'name') return system.name;
          if (property.startsWith('attribute.')) {
            const attrName = property.substring(10);
            const attr = system.attributes?.find(a => a.name === attrName);
            return attr ? String(attr.value) : match;
          }
        }
        return match;
      });
      
      // Pipe type placeholders
      const pipeTypeRegex = /{pipetype:([^.]+)\.([^}]+)}/g;
      displayText = displayText.replace(pipeTypeRegex, (match, pipeTypeId, property) => {
        const pipeType = pipeTypes.find(p => p.id === pipeTypeId);
        if (pipeType) {
          if (property === 'name') return pipeType.name;
          if (property === 'dn') return `DN ${pipeType.dn}`;
        }
        return match;
      });

      ctx.save();
      ctx.translate(screen.x, screen.y);
      ctx.rotate(annotation.rotation);

      // Set font
      ctx.font = `${annotation.fontSize * viewState.zoom * displaySettings.fontSizeMultiplier}px Arial`;
      ctx.fillStyle = annotation.color;
      ctx.textAlign = annotation.alignment;
      ctx.textBaseline = 'middle';

      // Measure text
      const textMetrics = ctx.measureText(displayText);
      const textWidth = textMetrics.width;
      const textHeight = annotation.fontSize * viewState.zoom * displaySettings.fontSizeMultiplier;

      // Draw background if specified
      if (annotation.backgroundColor && annotation.backgroundOpacity !== undefined) {
        const padding = 4 * viewState.zoom;
        const bgOpacity = annotation.backgroundOpacity;
        
        // Parse color and add opacity
        const hex = annotation.backgroundColor;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${bgOpacity})`;
        
        let bgX = -padding;
        let bgWidth = textWidth + padding * 2;
        
        if (annotation.alignment === 'center') {
          bgX = -textWidth / 2 - padding;
        } else if (annotation.alignment === 'right') {
          bgX = -textWidth - padding;
          bgWidth = textWidth + padding * 2;
        }
        
        ctx.fillRect(bgX, -textHeight / 2 - padding, bgWidth, textHeight + padding * 2);
      }

      // Draw text
      ctx.fillStyle = annotation.color;
      ctx.fillText(displayText, 0, 0);

      // Draw selection or hover outline
      if (isSelected || isHovered) {
        ctx.strokeStyle = isSelected ? '#0066ff' : '#999999';
        ctx.lineWidth = isSelected ? 2 : 1.5;
        ctx.setLineDash(isSelected ? [5, 5] : []);
        
        const padding = Math.max(10, annotation.fontSize * 0.5) * viewState.zoom;
        let outlineX = -padding;
        let outlineWidth = textWidth + padding * 2;
        
        if (annotation.alignment === 'center') {
          outlineX = -textWidth / 2 - padding;
        } else if (annotation.alignment === 'right') {
          outlineX = -textWidth - padding;
          outlineWidth = textWidth + padding * 2;
        }
        
        ctx.strokeRect(outlineX, -textHeight / 2 - padding, outlineWidth, textHeight + padding * 2);
        ctx.setLineDash([]);
      }

      ctx.restore();
    },
    [viewState, gridSize, displaySettings, systems, pipeTypes, projectName, hoveredTextId]
  );

  // Draw placed labels
  const drawLabels = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      // Check visibility toggle (default to true if undefined)
      if (displaySettings.showLabels === false) return;

      placedLabels.forEach((label) => {
        const template = labelTemplates.find((t) => t.id === label.templateId);
        if (!template) return;

        const isSelected = selectedLabelIds.includes(label.id);

        // Convert position to screen coordinates
        const worldPos = isoToScreen(label.position, gridSize);
        const screenPos = worldToScreen(worldPos, viewState);

        ctx.save();

        // Set font first for text measurement
        ctx.font = `${template.fontSize * viewState.zoom}px Arial`;
        
        // Calculate dynamic cell dimensions based on content
        const padding = 8 * viewState.zoom; // Padding inside cells
        const minCellHeight = (template.fontSize * 1.5) * viewState.zoom; // Minimum height based on font size
        
        // Measure all cells to find maximum width per column
        const columnWidths: number[] = new Array(template.columns).fill(0);
        const rowHeights: number[] = [];
        
        label.resolvedData.forEach((row, rowIndex) => {
          let maxRowHeight = minCellHeight;
          row.forEach((cellValue, colIndex) => {
            const textMetrics = ctx.measureText(cellValue);
            const textWidth = textMetrics.width + padding * 2;
            columnWidths[colIndex] = Math.max(columnWidths[colIndex], textWidth);
            
            // Height based on font size
            const textHeight = template.fontSize * viewState.zoom + padding * 2;
            maxRowHeight = Math.max(maxRowHeight, textHeight);
          });
          rowHeights.push(maxRowHeight);
        });

        // Calculate total table dimensions
        const tableWidth = columnWidths.reduce((sum, width) => sum + width, 0);
        const tableHeight = rowHeights.reduce((sum, height) => sum + height, 0);

        // Draw leader line to target element
        if (label.leaderLineVisible) {
          let targetScreenPos: Point | null = null;

          // Find target element position
          if (label.targetElementType === 'connection') {
            const connection = connections.find((c) => c.id === label.targetElementId);
            if (connection) {
              const targetWorld = isoToScreen(connection.position, gridSize);
              targetScreenPos = worldToScreen(targetWorld, viewState);
            }
          } else if (label.targetElementType === 'block') {
            const block = blocks.find((b) => b.id === label.targetElementId);
            if (block) {
              const targetWorld = isoToScreen(block.transform.position, gridSize);
              targetScreenPos = worldToScreen(targetWorld, viewState);
            }
          } else if (label.targetElementType === 'text-annotation') {
            const textAnnotation = textAnnotations.find((t) => t.id === label.targetElementId);
            if (textAnnotation) {
              const targetWorld = isoToScreen(textAnnotation.position, gridSize);
              targetScreenPos = worldToScreen(targetWorld, viewState);
            }
          }

          // Draw leader line
          if (targetScreenPos) {
            ctx.strokeStyle = label.leaderLineColor;
            ctx.lineWidth = label.leaderLineWidth * viewState.zoom;
            ctx.beginPath();
            ctx.moveTo(targetScreenPos.x, targetScreenPos.y);
            ctx.lineTo(screenPos.x, screenPos.y);
            ctx.stroke();

            // Draw small circle at target
            ctx.fillStyle = label.leaderLineColor;
            ctx.beginPath();
            ctx.arc(targetScreenPos.x, targetScreenPos.y, 3 * viewState.zoom, 0, Math.PI * 2);
            ctx.fill();
          }
        }

        // Draw table background
        ctx.fillStyle = template.backgroundColor;
        ctx.fillRect(screenPos.x, screenPos.y, tableWidth, tableHeight);

        // Draw table border
        ctx.strokeStyle = template.borderColor;
        ctx.lineWidth = template.borderWidth * viewState.zoom;
        ctx.strokeRect(screenPos.x, screenPos.y, tableWidth, tableHeight);

        // Draw cells
        ctx.fillStyle = template.textColor;
        ctx.textBaseline = 'middle';

        let currentY = screenPos.y;
        label.resolvedData.forEach((row, rowIndex) => {
          let currentX = screenPos.x;
          const rowHeight = rowHeights[rowIndex];
          const templateRow = template.rows[rowIndex];
          
          row.forEach((cellValue, colIndex) => {
            const cellWidth = columnWidths[colIndex];
            const cellAlign = templateRow?.[colIndex]?.align || 'center';

            // Draw cell border
            ctx.strokeStyle = template.borderColor;
            ctx.lineWidth = template.borderWidth * viewState.zoom;
            ctx.strokeRect(currentX, currentY, cellWidth, rowHeight);

            // Set text alignment based on cell settings
            ctx.textAlign = cellAlign;
            
            // Calculate text position based on alignment
            let textX = currentX + cellWidth / 2; // default: center
            if (cellAlign === 'left') {
              textX = currentX + (8 * viewState.zoom); // padding left
            } else if (cellAlign === 'right') {
              textX = currentX + cellWidth - (8 * viewState.zoom); // padding right
            }

            // Draw cell text
            ctx.fillStyle = template.textColor;
            ctx.fillText(
              cellValue,
              textX,
              currentY + rowHeight / 2
            );
            
            currentX += cellWidth;
          });
          
          currentY += rowHeight;
        });

        // Draw selection outline
        if (isSelected) {
          ctx.strokeStyle = '#0066ff';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(
            screenPos.x - 5,
            screenPos.y - 5,
            tableWidth + 10,
            tableHeight + 10
          );
          ctx.setLineDash([]);
        }

        ctx.restore();
      });

      // Draw preview label during placement (first step - before selecting element)
      if (labelPlacementStep === 'position-label' && labelPreviewPosition && selectedLabelTemplateId) {
        const template = labelTemplates.find((t) => t.id === selectedLabelTemplateId);
        if (template) {
          const worldPos = isoToScreen(labelPreviewPosition, gridSize);
          const screenPos = worldToScreen(worldPos, viewState);

          ctx.save();
          ctx.globalAlpha = 0.6;

          const cellWidth = template.cellWidth * viewState.zoom;
          const cellHeight = template.cellHeight * viewState.zoom;
          const tableWidth = template.columns * cellWidth;
          const tableHeight = template.rows.length * cellHeight;

          // Draw preview background
          ctx.fillStyle = template.backgroundColor;
          ctx.fillRect(screenPos.x, screenPos.y, tableWidth, tableHeight);

          // Draw preview border
          ctx.strokeStyle = template.borderColor;
          ctx.lineWidth = template.borderWidth * viewState.zoom;
          ctx.setLineDash([5, 3]);
          ctx.strokeRect(screenPos.x, screenPos.y, tableWidth, tableHeight);
          ctx.setLineDash([]);

          ctx.restore();
        }
      }
      
      // Draw instruction text when in element selection mode (step 2)
      if (labelPlacementStep === 'select-element') {
        ctx.save();
        
        // Highlight clickable elements with glowing circles
        ctx.globalAlpha = 0.3;
        ctx.fillStyle = 'rgba(0, 102, 255, 0.5)';
        
        // Highlight connections
        connections.forEach((connection) => {
          if (connection.position && connection.position.u !== undefined && connection.position.v !== undefined) {
            const worldPos = isoToScreen(connection.position, gridSize);
            const screenPos = worldToScreen(worldPos, viewState);
            const hitRadius = 50 * viewState.zoom;
            
            ctx.beginPath();
            ctx.arc(screenPos.x, screenPos.y, hitRadius, 0, Math.PI * 2);
            ctx.fill();
          }
        });
        
        // Highlight blocks
        blocks.forEach((block) => {
          const worldPos = isoToScreen(block.transform.position, gridSize);
          const screenPos = worldToScreen(worldPos, viewState);
          const hitRadius = 80 * viewState.zoom;
          
          ctx.beginPath();
          ctx.arc(screenPos.x, screenPos.y, hitRadius, 0, Math.PI * 2);
          ctx.fill();
        });
        
        ctx.restore();
      }
    },
    [
      placedLabels,
      labelTemplates,
      selectedLabelIds,
      selectedLabelTemplateId,
      viewState,
      gridSize,
      connections,
      blocks,
      textAnnotations,
      labelPlacementStep,
      labelTargetElement,
      labelPreviewPosition,
    ]
  );

  // Main render loop - EXACTLY like ISO.BLOCK
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = width;
    canvas.height = height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = backgroundSettings.backgroundColor;
    ctx.fillRect(0, 0, width, height);

    // Draw background
    drawBackground(ctx, width, height);

    // Draw all blocks
    blocks.forEach((block) => {
      const isSelected = selectedBlockIds.includes(block.id);
      drawBlock(ctx, block, isSelected);
    });

    // Draw connections (NACH den Blöcken, damit sie sichtbar sind)
    drawConnections(ctx);

    // Draw connection markers (Verbindungspunkte visualisieren)
    if (!isPrintMode) {
      drawConnectionMarkers(ctx);
    }

    // Draw text annotations
    textAnnotations.forEach((textAnnotation) => {
      const isSelected = !isPrintMode && selectedTextIds.includes(textAnnotation.id);
      const isHovered = !isPrintMode && hoveredTextId === textAnnotation.id;
      drawTextAnnotation(ctx, textAnnotation, isSelected, isHovered);
    });

    // Draw placed labels
    drawLabels(ctx);

    if (!isPrintMode) {
      // Draw available snap points (when dragging)
      drawSnapPoints(ctx);

      // Draw drag preview
      drawDragPreview(ctx);

      // Draw hover point
      drawHoverPoint(ctx);

      // Draw pipe preview
      drawPipePreview(ctx);

      // Draw selection box
      drawSelectionBox(ctx);
    }
  }, [
    blocks,
    selectedBlockIds,
    textAnnotations,
    selectedTextIds,
    viewState,
    backgroundSettings,
    draggedBlockData,
    dragPosition,
    snapTarget,
    hoverPoint,
    hoveredTextId,
    systemColors,
    drawBackground,
    drawConnections,
    drawConnectionMarkers,
    drawBlock,
    drawTextAnnotation,
    drawLabels,
    drawSnapPoints,
    drawDragPreview,
    drawHoverPoint,
    drawPipePreview,
    drawSelectionBox,
    pipeDrawStart,
    pipeDrawPreview,
    isPrintMode,
  ]);

  // Mouse handlers - EXACTLY like ISO.BLOCK

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;

      console.log('🖱️ Mouse down at screen:', screenX, screenY, 'button:', e.button, 'activeTool:', activeTool);

      // Pan with middle button or space
      if (e.button === 1 || (e.button === 0 && e.shiftKey)) {
        setIsPanning(true);
        setPanStart({ x: screenX, y: screenY });
        return;
      }

      // Left click - handle based on active tool
      if (e.button === 0) {
        const worldPos = screenToWorld({ x: screenX, y: screenY }, viewState);
        const isoPos = screenToIso(worldPos, gridSize);
        const snappedPos = snapEnabled ? snapToGrid(isoPos, 1) : isoPos; // Snap to integer grid if enabled

        // PIPE DRAW MODE
        if (activeTool === 'pipe-draw') {
          if (!pipeDrawStart) {
            // First click - set start point
            onPipeDrawStart?.(snappedPos);
          } else {
            // Second click - create pipe immediately with current coordinates
            console.log('🔧 PIPE: Creating from', pipeDrawStart, 'to', snappedPos);
            
            // Call onPipeDrawEnd with the end point as parameter
            onPipeDrawEnd?.(snappedPos);
          }
          return;
        }

        // TEXT MODE
        if (activeTool === 'text') {
          // Place text at clicked position
          onTextClick?.(snappedPos);
          return;
        }

        // LABEL MODE - NEW workflow: first place label, then select element
        if (activeTool === 'label' && selectedLabelTemplateId && onLabelAdd) {
          if (!labelPlacementStep || labelPlacementStep === 'position-label') {
            // Step 1: Place label at position (without target yet)
            const template = labelTemplates.find((t) => t.id === selectedLabelTemplateId);
            if (template) {
              // Create label with temporary empty data
              const newLabel: PlacedLabel = {
                id: `label-${Date.now()}`,
                templateId: template.id,
                targetElementType: 'block', // Temporary - will be updated
                targetElementId: 'temp-' + Date.now(), // Temporary ID
                position: snappedPos,
                leaderLineVisible: true,
                leaderLineColor: '#333333',
                leaderLineWidth: template.leaderLineWidth || 1, // Use template default
                resolvedData: template.rows.map(row => row.map(cell => cell.content)), // Show template content
              };

              onLabelAdd?.(newLabel);
              
              // Now go to element selection mode
              setLabelPlacementStep('select-element');
              setLabelTargetElement(null);
              setLabelPreviewPosition(null);
              
              // Store the label ID that was just placed
              (window as any).__justPlacedLabelId = newLabel.id;
              
              // Label placed, now select target element
            }
          } else if (labelPlacementStep === 'select-element') {
            // Step 2: Select target element to connect label
            let targetElement: { type: 'connection' | 'block' | 'text-annotation'; id: string } | null = null;

            // Looking for label target element

            // Check text annotations
            for (let i = textAnnotations.length - 1; i >= 0; i--) {
              const annotation = textAnnotations[i];
              const worldPos = isoToScreen(annotation.position, gridSize);
              const screenPos = worldToScreen(worldPos, viewState);
              const distance = Math.sqrt((screenX - screenPos.x) ** 2 + (screenY - screenPos.y) ** 2);
              const hitRadius = Math.max(20, annotation.fontSize) * viewState.zoom;
              
              if (distance < hitRadius) {
                targetElement = { type: 'text-annotation', id: annotation.id };
                break;
              }
            }

            // Check connections
            if (!targetElement) {
              for (const connection of connections) {
                // Safety check
                if (!connection.position || connection.position.u === undefined || connection.position.v === undefined) {
                  continue;
                }
                
                const worldPos = isoToScreen(connection.position, gridSize);
                const screenPos = worldToScreen(worldPos, viewState);
                const distance = Math.sqrt((screenX - screenPos.x) ** 2 + (screenY - screenPos.y) ** 2);
                const hitRadius = 50 * viewState.zoom; // Much more generous
                
                if (distance < hitRadius) {
                  targetElement = { type: 'connection', id: connection.id };
                  break;
                }
              }
            }

            // Check blocks
            if (!targetElement) {
              for (let i = blocks.length - 1; i >= 0; i--) {
                const block = blocks[i];
                const worldPos = isoToScreen(block.transform.position, gridSize);
                const screenPos = worldToScreen(worldPos, viewState);
                const distance = Math.sqrt((screenX - screenPos.x) ** 2 + (screenY - screenPos.y) ** 2);
                const hitRadius = 80 * viewState.zoom; // Much more generous
                
                if (distance < hitRadius) {
                  targetElement = { type: 'block', id: block.id };
                  break;
                }
              }
            }

            if (targetElement) {
              // Update the just-placed label with the selected target
              const labelId = (window as any).__justPlacedLabelId;
              if (labelId && onLabelUpdate) {
                const template = labelTemplates.find((t) => t.id === selectedLabelTemplateId);
                if (template) {
                  const resolvedData = resolveLabelData(
                    template,
                    targetElement.type,
                    targetElement.id,
                    blocks,
                    connections,
                    textAnnotations,
                    systems
                  );



                  onLabelUpdate(labelId, {
                    targetElementType: targetElement.type,
                    targetElementId: targetElement.id,
                    resolvedData,
                  });
                }
              }
              
              // Reset placement state
              setLabelPlacementStep(null);
              setLabelTargetElement(null);
              (window as any).__justPlacedLabelId = null;

            } else {
              console.warn('⚠️ No target element found at click position');
              // Don't reset - allow user to try clicking again
            }
          }
          return;
        }

        // SELECT MODE with filter and cycle-through logic
        
        // Check if we clicked at the same position (for cycle-through)
        const clickTolerance = 5; // pixels tolerance for "same position"
        const isSamePosition = lastClickPosition && 
          Math.abs(screenX - lastClickPosition.x) < clickTolerance &&
          Math.abs(screenY - lastClickPosition.y) < clickTolerance;
        
        // Reset cycle index if different position or if Ctrl/Shift is pressed (multi-select)
        if (!isSamePosition || e.ctrlKey || e.metaKey || e.shiftKey) {
          setClickCycleIndex(0);
        }
        
        // Update last click position
        setLastClickPosition({ x: screenX, y: screenY });
        
        // Collect all clickable elements at this position (in priority order)
        interface ClickableElement {
          type: 'dimension' | 'text' | 'connection' | 'block';
          id: string;
          blockId?: string; // For dimensions
          dimensionId?: string; // For dimensions
        }
        const clickableElements: ClickableElement[] = [];
        
        // 1. Check dimensions (if not filtered)
        if (!filterConnectionsOnly && showDimensions && onDimensionClick) {
          for (let i = blocks.length - 1; i >= 0; i--) {
            const block = blocks[i];
            for (const el of block.blockData.elements) {
              if (el.type === 'dimension') {
                const start = transformPoint(el.start, block.transform);
                const end = transformPoint(el.end, block.transform);
                
                // Calculate dimension text position (midpoint)
                const midU = (start.u + end.u) / 2;
                const midV = (start.v + end.v) / 2;
                
                // Hit test: check if click is near the text (within 1 grid unit)
                const du = Math.abs(isoPos.u - midU);
                const dv = Math.abs(isoPos.v - midV);
                
                if (du < 1 && dv < 1) {
                  clickableElements.push({
                    type: 'dimension',
                    id: el.id,
                    blockId: block.id,
                    dimensionId: el.id,
                  });
                }
              }
            }
          }
        }

        // 1.5. Check labels (always selectable, even in connection filter mode)
        if (onLabelSelect) {
          for (let i = placedLabels.length - 1; i >= 0; i--) {
            const label = placedLabels[i];
            const template = labelTemplates.find((t) => t.id === label.templateId);
            if (!template) continue;

            const worldPos = isoToScreen(label.position, gridSize);
            const screenPos = worldToScreen(worldPos, viewState);

            // Calculate dynamic table dimensions (same logic as in rendering)
            const canvas = canvasRef.current;
            if (!canvas) continue;
            const ctx = canvas.getContext('2d');
            if (!ctx) continue;
            
            ctx.font = `${template.fontSize * viewState.zoom}px Arial`;
            const padding = 8 * viewState.zoom;
            const minCellHeight = (template.fontSize * 1.5) * viewState.zoom;
            
            const columnWidths: number[] = new Array(template.columns).fill(0);
            const rowHeights: number[] = [];
            
            label.resolvedData.forEach((row) => {
              let maxRowHeight = minCellHeight;
              row.forEach((cellValue, colIndex) => {
                const textMetrics = ctx.measureText(cellValue);
                const textWidth = textMetrics.width + padding * 2;
                columnWidths[colIndex] = Math.max(columnWidths[colIndex], textWidth);
                const textHeight = template.fontSize * viewState.zoom + padding * 2;
                maxRowHeight = Math.max(maxRowHeight, textHeight);
              });
              rowHeights.push(maxRowHeight);
            });
            
            const tableWidth = columnWidths.reduce((sum, width) => sum + width, 0);
            const tableHeight = rowHeights.reduce((sum, height) => sum + height, 0);

            // Check if click is inside label bounds
            if (
              screenX >= screenPos.x &&
              screenX <= screenPos.x + tableWidth &&
              screenY >= screenPos.y &&
              screenY <= screenPos.y + tableHeight
            ) {
              clickableElements.push({
                type: 'label' as any,
                id: label.id,
              });
            }
          }
        }

        // 2. Check text annotations (if not filtered)
        if (!filterConnectionsOnly) {
          const screenPoint = { x: screenX, y: screenY };
          for (let i = textAnnotations.length - 1; i >= 0; i--) {
            const annotation = textAnnotations[i];
            if (isPointInTextAnnotation(annotation, screenPoint)) {
              clickableElements.push({
                type: 'text',
                id: annotation.id,
              });
            }
          }
        }

        // 2.5. Check connections (nur wenn Verbindungs-Modus aktiv ist)
        if (filterConnectionsOnly) {
          const connClickTolerance = 15; // pixels
          for (let i = connections.length - 1; i >= 0; i--) {
            const connection = connections[i];
            
            // Sicherheitsprüfung: Überspringe Connections ohne Position
            if (!connection.position || connection.position.u === undefined || connection.position.v === undefined) {
              continue;
            }
          
          const world = isoToScreen(connection.position, gridSize);
          const screen = worldToScreen(world, viewState);
          
          // Berechne Radius wie beim Rendering
          let radius = 8;
          if (connection.diameter) {
            radius = Math.max(5, Math.min(20, connection.diameter / 10));
          } else if (connection.dn) {
            radius = Math.max(5, Math.min(20, connection.dn / 10));
          }
          
          const dx = screenX - screen.x;
          const dy = screenY - screen.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance <= connClickTolerance + radius * viewState.zoom * displaySettings.connectionSizeMultiplier) {
            clickableElements.push({
              type: 'connection',
              id: connection.id,
            });
          }
        }
        }

        // 3. Old connection check removed - now handled in step 2.5 above

        // 4. Check blocks (if not filtered)
        if (!filterConnectionsOnly) {
          for (let i = blocks.length - 1; i >= 0; i--) {
            const block = blocks[i];
            // Simple hit test - check if near any element
            const isNear = block.blockData.elements.some((el) => {
              if (el.type === 'connection') {
                const pos = transformPoint(el.position, block.transform);
                const du = Math.abs(isoPos.u - pos.u);
                const dv = Math.abs(isoPos.v - pos.v);
                return du < 1 && dv < 1;
              } else if (el.type === 'line') {
                const start = transformPoint(el.start, block.transform);
                const end = transformPoint(el.end, block.transform);
                // Bounding box check
                const minU = Math.min(start.u, end.u) - 0.5;
                const maxU = Math.max(start.u, end.u) + 0.5;
                const minV = Math.min(start.v, end.v) - 0.5;
                const maxV = Math.max(start.v, end.v) + 0.5;
                return (
                  isoPos.u >= minU &&
                  isoPos.u <= maxU &&
                  isoPos.v >= minV &&
                  isoPos.v <= maxV
                );
              } else if (el.type === 'shape') {
                const points = el.points.map((p) => transformPoint(p, block.transform));
                const minU = Math.min(...points.map((p) => p.u)) - 0.5;
                const maxU = Math.max(...points.map((p) => p.u)) + 0.5;
                const minV = Math.min(...points.map((p) => p.v)) - 0.5;
                const maxV = Math.max(...points.map((p) => p.v)) + 0.5;
                return (
                  isoPos.u >= minU &&
                  isoPos.u <= maxU &&
                  isoPos.v >= minV &&
                  isoPos.v <= maxV
                );
              }
              return false;
            });

            if (isNear) {
              clickableElements.push({
                type: 'block',
                id: block.id,
              });
              break; // Only take the first (topmost) block
            }
          }
        }

        // Found clickable elements

        // Now decide which element to select based on mode and cycle index
        let elementToSelect: ClickableElement | null = null;

        if (clickableElements.length > 0) {
          if (isSamePosition && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
            // Cycle through elements at the same position
            const cycleIndex = clickCycleIndex % clickableElements.length;
            elementToSelect = clickableElements[cycleIndex];
            setClickCycleIndex(cycleIndex + 1);
          } else {
            // First click or modifier key pressed - select first element
            elementToSelect = clickableElements[0];
          }
        }

        // Handle the selected element
        if (elementToSelect) {
          if (elementToSelect.type === 'dimension') {
            console.log('📏 DIMENSION CLICK:', elementToSelect.dimensionId);
            onDimensionClick?.(elementToSelect.blockId!, elementToSelect.dimensionId!);
            return;
          } else if (elementToSelect.type === 'text') {
            // Text annotation clicked
            if (e.ctrlKey || e.metaKey) {
              // Multi-select
              if (selectedTextIds.includes(elementToSelect.id)) {
                onTextSelect?.(selectedTextIds.filter((id) => id !== elementToSelect.id));
              } else {
                onTextSelect?.([...selectedTextIds, elementToSelect.id]);
              }
            } else {
              // Single select and start drag
              if (!selectedTextIds.includes(elementToSelect.id)) {
                onTextSelect?.([elementToSelect.id]);
                // Deselect blocks when selecting text
                onBlockSelect([]);
              }
              // UNIFIED DRAG SYSTEM: Store positions of ALL selected texts
              const startPositions = new Map<string, DragItem>();
              const textsToMove = selectedTextIds.includes(elementToSelect.id) 
                ? selectedTextIds 
                : [elementToSelect.id];
              
              textsToMove.forEach(textId => {
                const annotation = textAnnotations.find(t => t.id === textId);
                if (annotation) {
                  startPositions.set(textId, { 
                    position: { ...annotation.position },
                    type: 'text'
                  });
                }
              });
              
              setIsDragging(true);
              setDragBlockId(elementToSelect.id);
              dragStartPositionsRef.current = startPositions;
              dragStartMousePosRef.current = { x: screenX, y: screenY };
              setDragUpdateCounter(c => c + 1); // Force re-render
              setLastMousePos({ x: screenX, y: screenY });
            }
            return;
          } else if (elementToSelect.type === 'label' as any) {
            // Label clicked
            if (e.ctrlKey || e.metaKey) {
              // Multi-select
              if (selectedLabelIds.includes(elementToSelect.id)) {
                onLabelSelect?.(selectedLabelIds.filter((id) => id !== elementToSelect.id));
              } else {
                onLabelSelect?.([...selectedLabelIds, elementToSelect.id]);
              }
            } else {
              // Single select and start drag
              if (!selectedLabelIds.includes(elementToSelect.id)) {
                // Debug: Print label info
                const selectedLabel = placedLabels.find(l => l.id === elementToSelect.id);
                if (selectedLabel) {
                  console.log('🏷️ Selected Label:', {
                    id: selectedLabel.id,
                    templateId: selectedLabel.templateId,
                    targetType: selectedLabel.targetElementType,
                    targetId: selectedLabel.targetElementId,
                    resolvedData: selectedLabel.resolvedData
                  });
                  
                  // Check if target exists
                  if (selectedLabel.targetElementType === 'connection') {
                    const conn = connections.find(c => c.id === selectedLabel.targetElementId);
                    if (conn) {
                      console.log('🔌 Target Connection:', {
                        id: conn.id,
                        dn: conn.dn,
                        systemId: conn.systemId,
                        attributes: conn.attributes
                      });
                    } else {
                      console.warn('⚠️ Label references non-existent connection:', selectedLabel.targetElementId, '(Label:', selectedLabel.id, ')');
                      console.warn('   → This label should be cleaned up or updated');
                    }
                  }
                }
                
                onLabelSelect?.([elementToSelect.id]);
                // Deselect other elements when selecting label
                onBlockSelect([]);
                onTextSelect?.([]);
                onConnectionSelect?.([]);
              }
            }
            
            // Start dragging ALL selected labels (moved outside)
            if (!e.ctrlKey && !e.metaKey) {
              // UNIFIED DRAG SYSTEM: Store positions of ALL selected labels
              const startPositions = new Map<string, DragItem>();
              const labelsToMove = selectedLabelIds.includes(elementToSelect.id) 
                ? selectedLabelIds 
                : [elementToSelect.id];
              
              labelsToMove.forEach(labelId => {
                const label = placedLabels.find(l => l.id === labelId);
                if (label) {
                  startPositions.set(labelId, { 
                    position: { ...label.position },
                    type: 'label'
                  });
                }
              });
              
              setIsDragging(true);
              // DON'T set dragBlockId for labels - keep it null
              dragStartPositionsRef.current = startPositions;
              dragStartMousePosRef.current = { x: screenX, y: screenY };
              setDragUpdateCounter(c => c + 1); // Force re-render
              setLastMousePos({ x: screenX, y: screenY });
            }
            return;
          } else if (elementToSelect.type === 'connection') {
            // Connection clicked directly
            if (e.ctrlKey || e.metaKey) {
              // Multi-select
              if (selectedConnectionIds.includes(elementToSelect.id)) {
                onConnectionSelect?.(selectedConnectionIds.filter((id) => id !== elementToSelect.id));
              } else {
                onConnectionSelect?.([...selectedConnectionIds, elementToSelect.id]);
              }
            } else {
              // Single select
              onConnectionSelect?.([elementToSelect.id]);
              // Deselect blocks and text when selecting connection
              onBlockSelect([]);
              onTextSelect?.([]);
            }
            return;
          } else if (elementToSelect.type === 'block') {
            // Block clicked
            if (e.ctrlKey || e.metaKey) {
              // Add/remove from selection
              if (selectedBlockIds.includes(elementToSelect.id)) {
                onBlockSelect(selectedBlockIds.filter((id) => id !== elementToSelect.id));
              } else {
                onBlockSelect([...selectedBlockIds, elementToSelect.id]);
              }
            } else if (e.shiftKey) {
              // Add to selection
              if (!selectedBlockIds.includes(elementToSelect.id)) {
                onBlockSelect([...selectedBlockIds, elementToSelect.id]);
              }
            } else {
              // Replace selection and start drag
              if (!selectedBlockIds.includes(elementToSelect.id)) {
                onBlockSelect([elementToSelect.id]);
                // Deselect text when selecting blocks
                onTextSelect?.([]);
              }
              // UNIFIED DRAG SYSTEM: Store positions of ALL selected elements (blocks, texts, labels)
              const startPositions = new Map<string, DragItem>();
              
              // 1. Store all selected blocks
              const blocksToMove = selectedBlockIds.includes(elementToSelect.id) 
                ? selectedBlockIds 
                : [elementToSelect.id];
              blocksToMove.forEach(blockId => {
                const block = blocks.find(b => b.id === blockId);
                if (block) {
                  startPositions.set(blockId, { 
                    position: { ...block.transform.position },
                    type: 'block'
                  });
                }
              });
              
              // 2. Store all selected text annotations
              selectedTextIds.forEach(textId => {
                const annotation = textAnnotations.find(t => t.id === textId);
                if (annotation) {
                  startPositions.set(textId, {
                    position: { ...annotation.position },
                    type: 'text'
                  });
                }
              });
              
              // 3. Store all selected labels
              selectedLabelIds.forEach(labelId => {
                const label = placedLabels.find(l => l.id === labelId);
                if (label) {
                  startPositions.set(labelId, {
                    position: { ...label.position },
                    type: 'label'
                  });
                }
              });
              
              console.log('🖱️ DRAG START:', startPositions.size, 'elements');
              
              // Start dragging
              setIsDragging(true);
              setDragBlockId(elementToSelect.id);
              dragStartPositionsRef.current = startPositions;
              dragStartMousePosRef.current = { x: screenX, y: screenY };
              setDragUpdateCounter(c => c + 1); // Force re-render
              setLastMousePos({ x: screenX, y: screenY });
            }
            return;
          }
        } else {
          // Clicked empty space - start box selection
          if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
            onBlockSelect([]);
            onTextSelect?.([]);
            onConnectionSelect?.([]);
            onLabelSelect?.([]);
          }
          setIsSelecting(true);
          setSelectionStart({ x: screenX, y: screenY });
          setSelectionEnd({ x: screenX, y: screenY });
        }
      }
    },
    [viewState, gridSize, blocks, connections, selectedBlockIds, selectedConnectionIds, textAnnotations, selectedTextIds, placedLabels, labelTemplates, selectedLabelIds, selectedLabelTemplateId, systems, onBlockSelect, onConnectionSelect, onTextSelect, onTextClick, onLabelSelect, onLabelAdd, activeTool, pipeDrawStart, onPipeDrawStart, onPipeDrawPreview, onPipeDrawEnd, showDimensions, onDimensionClick, isPointInTextAnnotation, snapEnabled, getTransformedSockets, getTransformedLineEndpoints, filterConnectionsOnly, lastClickPosition, clickCycleIndex, labelPlacementStep, labelTargetElement]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;

      // Calculate world/iso position for general use
      const currentWorldPos = screenToWorld({ x: screenX, y: screenY }, viewState);
      const currentIsoPos = screenToIso(currentWorldPos, gridSize);
      
      // Notify parent about mouse position (for paste operations etc)
      onMouseMove?.(currentIsoPos);

      // Panning
      if (isPanning && panStart) {
        const dx = screenX - panStart.x;
        const dy = screenY - panStart.y;

        onViewStateChange({
          ...viewState,
          panX: viewState.panX + dx,
          panY: viewState.panY + dy,
        });

        setPanStart({ x: screenX, y: screenY });
        return;
      }

      // PIPE DRAW MODE - Update preview
      if (activeTool === 'pipe-draw' && pipeDrawStart) {
        const worldPos = screenToWorld({ x: screenX, y: screenY }, viewState);
        const isoPos = screenToIso(worldPos, gridSize);
        const snappedPos = snapEnabled ? snapToGrid(isoPos, 1) : isoPos; // Snap to integer grid if enabled
        
        // Mouse move in pipe-draw mode
        
        onPipeDrawPreview?.(snappedPos);
        return;
      }

      // LABEL PLACEMENT MODE - Update preview position (first step)
      if (activeTool === 'label' && (!labelPlacementStep || labelPlacementStep === 'position-label')) {
        const worldPos = screenToWorld({ x: screenX, y: screenY }, viewState);
        const isoPos = screenToIso(worldPos, gridSize);
        const snappedPos = snapEnabled ? snapToGrid(isoPos, 1) : isoPos;
        setLabelPreviewPosition(snappedPos);
        setLabelPlacementStep('position-label'); // Ensure step is set
        return;
      }

      // Box selection
      if (isSelecting && selectionStart) {
        setSelectionEnd({ x: screenX, y: screenY });
        return;
      }

      // ═══════════════════════════════════════════════════════════════════
      // UNIFIED DRAG SYSTEM: Handles blocks, texts, and labels together
      // ═══════════════════════════════════════════════════════════════════
      if (isDragging && dragStartPositionsRef.current.size > 0 && dragStartMousePosRef.current) {
        // Calculate total delta from drag start position
        const dx = screenX - dragStartMousePosRef.current.x;
        const dy = screenY - dragStartMousePosRef.current.y;
        
        // Convert screen delta to world delta
        const worldDelta = {
          x: dx / viewState.zoom,
          y: dy / viewState.zoom
        };
        
        // Convert world delta to iso delta
        const isoDelta = {
          u: (worldDelta.x / Math.cos(30 * Math.PI / 180) + worldDelta.y / Math.sin(30 * Math.PI / 180)) / (2 * gridSize),
          v: (worldDelta.y / Math.sin(30 * Math.PI / 180) - worldDelta.x / Math.cos(30 * Math.PI / 180)) / (2 * gridSize)
        };

        // Move ALL elements from their original start positions
        dragStartPositionsRef.current.forEach((dragItem, elementId) => {
          const newPosition = {
            u: dragItem.position.u + isoDelta.u,
            v: dragItem.position.v + isoDelta.v,
          };
          
          // Call the appropriate update function based on element type
          switch (dragItem.type) {
            case 'block': {
              const block = blocks.find(b => b.id === elementId);
              if (block) {
                onBlockUpdate(elementId, {
                  transform: {
                    ...block.transform,
                    position: snapEnabled ? snapToGrid(newPosition) : newPosition,
                  },
                });
              }
              break;
            }
            case 'text': {
              onTextUpdate?.(elementId, {
                position: snapEnabled ? snapToGrid(newPosition) : newPosition,
              });
              break;
            }
            case 'label': {
              onLabelUpdate?.(elementId, {
                position: snapEnabled ? snapToGrid(newPosition) : newPosition,
              });
              break;
            }
          }
        });

        setLastMousePos({ x: screenX, y: screenY });
        return;
      }

      // OLD: Dragging single block - MIT SOCKET-SNAPPING
      if (isDragging && dragBlockId && dragOffset) {
        const worldPos = screenToWorld({ x: screenX, y: screenY }, viewState);
        const isoPos = screenToIso(worldPos, gridSize);
        const snappedPos = snapEnabled ? snapToGrid(isoPos) : isoPos;

        // Berechne initiale neue Position basierend auf Drag-Offset
        const basePosition = {
          u: snappedPos.u - dragOffset.u,
          v: snappedPos.v - dragOffset.v,
        };

        // Socket-zu-Socket + Line-to-Line Snapping beim Verschieben:
        const draggedBlock = blocks.find((b) => b.id === dragBlockId);
        if (draggedBlock) {
          // 1. Erstelle temporäre Transform mit neuer Position
          const tempTransform = {
            ...draggedBlock.transform,
            position: basePosition,
          };

          // 2. Verwende die neue findBestSnap Funktion
          const allExistingSockets = getAllSockets(dragBlockId);
          const allExistingLineEndpoints = getAllLineEndpoints(dragBlockId);
          
          const snapResult = findBestSnap(
            draggedBlock.blockData,
            tempTransform,
            basePosition,
            allExistingSockets,
            allExistingLineEndpoints,
            0.8
          );

          // Verwende Snap-Position wenn gefunden, sonst base Position
          const finalPosition = snapResult.bestSnapPosition || basePosition;

          onBlockUpdate(dragBlockId, {
            transform: {
              ...draggedBlock.transform,
              position: finalPosition,
            },
          });

          // Update visual feedback - store connection info for handleMouseUp
          if (snapResult.bestSnapPosition && snapResult.bestTargetConnection && snapResult.bestSourceConnection) {
            setSnapTarget(snapResult.bestTargetConnection as any);
            // Store source connection info for handleMouseUp
            (window as any).__pendingConnection = {
              source: snapResult.bestSourceConnection,
              target: snapResult.bestTargetConnection,
              sourceBlockId: dragBlockId,
            };
          } else {
            setSnapTarget(null);
            (window as any).__pendingConnection = null;
          }
        } else {
          // Fallback wenn Block nicht gefunden
          const block = blocks.find((b) => b.id === dragBlockId);
          if (block) {
            onBlockUpdate(dragBlockId, {
              transform: {
                ...block.transform,
                position: basePosition,
              },
            });
          }
        }
        return;
      }

      // Update hover point
      const worldPos = screenToWorld({ x: screenX, y: screenY }, viewState);
      const isoPos = screenToIso(worldPos, gridSize);
      const snappedPos = snapEnabled ? snapToGrid(isoPos) : isoPos;
      setHoverPoint(snappedPos);
      
      // Update hover state for text annotations (for cursor and visual feedback)
      if (!isDragging && !isSelecting && !isPanning) {
        const screenPoint = { x: screenX, y: screenY };
        let foundHoveredText = false;
        
        for (let i = textAnnotations.length - 1; i >= 0; i--) {
          const annotation = textAnnotations[i];
          if (isPointInTextAnnotation(annotation, screenPoint)) {
            setHoveredTextId(annotation.id);
            foundHoveredText = true;
            break;
          }
        }
        
        if (!foundHoveredText) {
          setHoveredTextId(null);
        }
      }
    },
    [
      isPanning,
      panStart,
      isDragging,
      dragBlockId,
      dragOffset,
      dragUpdateCounter, // ✅ Include counter to force re-creation when drag starts
      viewState,
      gridSize,
      blocks,
      onViewStateChange,
      onBlockUpdate,
      onTextUpdate,
      onLabelUpdate,
      getAllSockets,
      getAllLineEndpoints,
      activeTool,
      pipeDrawStart,
      onPipeDrawPreview,
      isSelecting,
      selectionStart,
      selectedBlockIds,
      selectedTextIds,
      textAnnotations,
      lastMousePos,
      isPointInTextAnnotation,
      snapEnabled,
      labelPlacementStep,
      labelPreviewPosition,
    ]
  );

  const handleMouseUp = useCallback(() => {
    // Connection creation is now handled automatically by detectConnections in BlockSystemEditor
    // Clear any pending connection state from drag&drop
    if (isDragging && dragBlockId && (window as any).__pendingConnection) {
      (window as any).__pendingConnection = null;
      setSnapTarget(null);
    }

    // Complete box selection
    if (isSelecting && selectionStart && selectionEnd) {
      const minX = Math.min(selectionStart.x, selectionEnd.x);
      const minY = Math.min(selectionStart.y, selectionEnd.y);
      const maxX = Math.max(selectionStart.x, selectionEnd.x);
      const maxY = Math.max(selectionStart.y, selectionEnd.y);

      const box = { minX, minY, maxX, maxY };
      
      // Find all blocks in box
      const selectedBlocksInBox = blocks
        .filter(block => isBlockInBox(block, box))
        .map(block => block.id);

      // Find all text annotations in box
      const selectedTextsInBox = textAnnotations
        .filter(annotation => isTextInBox(annotation, box))
        .map(annotation => annotation.id);

      // Find all connections in box
      const selectedConnectionsInBox = connections
        .filter(conn => isConnectionInBox(conn, box))
        .map(conn => conn.id);

      // Find all labels in box
      const selectedLabelsInBox = placedLabels
        .filter(label => {
          const world = isoToScreen(label.position, gridSize);
          const screen = worldToScreen(world, viewState);
          return screen.x >= box.minX && screen.x <= box.maxX &&
                 screen.y >= box.minY && screen.y <= box.maxY;
        })
        .map(label => label.id);

      // Merge with existing selection
      const newBlockSelection = [...new Set([...selectedBlockIds, ...selectedBlocksInBox])];
      const newTextSelection = [...new Set([...selectedTextIds, ...selectedTextsInBox])];
      const newConnectionSelection = [...new Set([...selectedConnectionIds, ...selectedConnectionsInBox])];
      const newLabelSelection = [...new Set([...selectedLabelIds, ...selectedLabelsInBox])];
      
      onBlockSelect(newBlockSelection);
      onTextSelect?.(newTextSelection);
      onConnectionSelect?.(newConnectionSelection);
      onLabelSelect?.(newLabelSelection);
      
      console.log('📦 BOX SELECT:', selectedBlocksInBox.length, 'blocks,', selectedTextsInBox.length, 'texts,', selectedConnectionsInBox.length, 'conns,', selectedLabelsInBox.length, 'labels');
      
      setIsSelecting(false);
      setSelectionStart(null);
      setSelectionEnd(null);
    }

    setIsPanning(false);
    setPanStart(null);
    setIsDragging(false);
    setDragBlockId(null);
    setDragOffset(null);
    dragStartPositionsRef.current = new Map();
    dragStartMousePosRef.current = null;
  }, [
    isDragging, 
    dragBlockId, 
    isSelecting, 
    selectionStart, 
    selectionEnd, 
    blocks, 
    textAnnotations, 
    connections, 
    placedLabels,
    isBlockInBox, 
    isTextInBox, 
    isConnectionInBox, 
    selectedBlockIds, 
    selectedTextIds, 
    selectedConnectionIds, 
    selectedLabelIds,
    onBlockSelect, 
    onTextSelect, 
    onConnectionSelect,
    onLabelSelect,
    viewState,
    gridSize
  ]);

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const screenPoint = { x: screenX, y: screenY };

      // Check if double-clicking on a label
      for (let i = placedLabels.length - 1; i >= 0; i--) {
        const label = placedLabels[i];
        const template = labelTemplates.find((t) => t.id === label.templateId);
        if (!template) continue;

        const worldPos = isoToScreen(label.position, gridSize);
        const screenPos = worldToScreen(worldPos, viewState);

        // Calculate dynamic table dimensions
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        
        ctx.font = `${template.fontSize * viewState.zoom}px Arial`;
        const padding = 8 * viewState.zoom;
        const minCellHeight = (template.fontSize * 1.5) * viewState.zoom;
        
        const columnWidths: number[] = new Array(template.columns).fill(0);
        const rowHeights: number[] = [];
        
        label.resolvedData.forEach((row) => {
          let maxRowHeight = minCellHeight;
          row.forEach((cellValue, colIndex) => {
            const textMetrics = ctx.measureText(cellValue);
            const textWidth = textMetrics.width + padding * 2;
            columnWidths[colIndex] = Math.max(columnWidths[colIndex], textWidth);
            const textHeight = template.fontSize * viewState.zoom + padding * 2;
            maxRowHeight = Math.max(maxRowHeight, textHeight);
          });
          rowHeights.push(maxRowHeight);
        });
        
        const tableWidth = columnWidths.reduce((sum, width) => sum + width, 0);
        const tableHeight = rowHeights.reduce((sum, height) => sum + height, 0);

        // Check if click is inside label bounds
        if (
          screenX >= screenPos.x &&
          screenX <= screenPos.x + tableWidth &&
          screenY >= screenPos.y &&
          screenY <= screenPos.y + tableHeight
        ) {
          onLabelEdit?.(label.id);
          return;
        }
      }

      // Check if double-clicking on a text annotation
      for (let i = textAnnotations.length - 1; i >= 0; i--) {
        const annotation = textAnnotations[i];
        if (isPointInTextAnnotation(annotation, screenPoint)) {
          onTextEdit?.(annotation.id);
          return;
        }
      }
    },
    [textAnnotations, placedLabels, labelTemplates, isPointInTextAnnotation, onTextEdit, onLabelEdit, viewState, gridSize]
  );

  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLCanvasElement>) => {
      e.preventDefault();

      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Calculate zoom - EXACTLY like ISO.BLOCK
      const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.01, Math.min(10, viewState.zoom * zoomFactor));

      // Zoom towards mouse position - EXACTLY like ISO.BLOCK
      const worldPosBeforeZoom = screenToWorld({ x: mouseX, y: mouseY }, viewState);
      const newPanX = mouseX - worldPosBeforeZoom.x * newZoom;
      const newPanY = mouseY - worldPosBeforeZoom.y * newZoom;

      onViewStateChange({
        zoom: newZoom,
        panX: newPanX,
        panY: newPanY,
      });
    },
    [viewState, onViewStateChange]
  );

  // Drag & Drop handlers for library blocks
  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';

      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;

      const worldPos = screenToWorld({ x: screenX, y: screenY }, viewState);
      const isoPos = screenToIso(worldPos, gridSize);
      const snappedPos = snapEnabled ? snapToGrid(isoPos) : isoPos;

      try {
        const data = e.dataTransfer.getData('application/json');
        if (data) {
          const blockData: IsomPart = JSON.parse(data);
          setDraggedBlockData(blockData);

          // Socket/Line-zu-Socket/Line Snapping:
          // 1. Erstelle temporäre Transform für das zu platzierende Bauteil
          const tempTransform = createDefaultTransform(snappedPos);
          
          // 2. Hole alle Sockets UND Line-Endpoints des zu platzierenden Bauteils
          const draggedSockets = getTransformedSockets(blockData.elements, tempTransform);
          const draggedLineEndpoints = getTransformedLineEndpoints(blockData.elements, tempTransform);
          
          // 3. Hole alle Sockets UND Line-Endpoints der existierenden Bauteile
          const allExistingSockets = getAllSockets();
          const allExistingLineEndpoints = getAllLineEndpoints();
          
          // 4. Finde das nächstgelegene Verbindungspunkt-Paar (Socket-Socket, Socket-Line, Line-Line)
          let closestDistance = 0.8; // Snap-Threshold in Grid-Units
          let bestSnapPosition: IsoPoint | null = null;
          let bestTargetConnection: { 
            type: 'socket' | 'line';
            blockId: string; 
            socketIndex?: number;
            lineIndex?: number;
            isStart?: boolean;
          } | null = null;
          
          // Test 1: Dragged Sockets → Existing Sockets
          draggedSockets.forEach((draggedSocket) => {
            allExistingSockets.forEach((existingSocket) => {
              const du = draggedSocket.position.u - existingSocket.position.u;
              const dv = draggedSocket.position.v - existingSocket.position.v;
              const distance = Math.sqrt(du * du + dv * dv);
              
              if (distance < closestDistance) {
                closestDistance = distance;
                const socketOffset = {
                  u: draggedSocket.position.u - snappedPos.u,
                  v: draggedSocket.position.v - snappedPos.v,
                };
                bestSnapPosition = {
                  u: existingSocket.position.u - socketOffset.u,
                  v: existingSocket.position.v - socketOffset.v,
                };
                bestTargetConnection = {
                  type: 'socket',
                  blockId: existingSocket.blockId,
                  socketIndex: existingSocket.socketIndex,
                };
              }
            });
          });

          // Test 2: Dragged Sockets → Existing Line Endpoints
          draggedSockets.forEach((draggedSocket) => {
            allExistingLineEndpoints.forEach((existingEndpoint) => {
              const du = draggedSocket.position.u - existingEndpoint.position.u;
              const dv = draggedSocket.position.v - existingEndpoint.position.v;
              const distance = Math.sqrt(du * du + dv * dv);
              
              if (distance < closestDistance) {
                closestDistance = distance;
                const socketOffset = {
                  u: draggedSocket.position.u - snappedPos.u,
                  v: draggedSocket.position.v - snappedPos.v,
                };
                bestSnapPosition = {
                  u: existingEndpoint.position.u - socketOffset.u,
                  v: existingEndpoint.position.v - socketOffset.v,
                };
                bestTargetConnection = {
                  type: 'line',
                  blockId: existingEndpoint.blockId,
                  lineIndex: existingEndpoint.lineIndex,
                  isStart: existingEndpoint.isStart,
                };
              }
            });
          });

          // Test 3: Dragged Line Endpoints → Existing Sockets
          draggedLineEndpoints.forEach((draggedLine) => {
            [draggedLine.startPosition, draggedLine.endPosition].forEach((endpointPos, idx) => {
              allExistingSockets.forEach((existingSocket) => {
                const du = endpointPos.u - existingSocket.position.u;
                const dv = endpointPos.v - existingSocket.position.v;
                const distance = Math.sqrt(du * du + dv * dv);
                
                if (distance < closestDistance) {
                  closestDistance = distance;
                  const endpointOffset = {
                    u: endpointPos.u - snappedPos.u,
                    v: endpointPos.v - snappedPos.v,
                  };
                  bestSnapPosition = {
                    u: existingSocket.position.u - endpointOffset.u,
                    v: existingSocket.position.v - endpointOffset.v,
                  };
                  bestTargetConnection = {
                    type: 'socket',
                    blockId: existingSocket.blockId,
                    socketIndex: existingSocket.socketIndex,
                  };
                }
              });
            });
          });

          // Test 4: Dragged Line Endpoints → Existing Line Endpoints
          draggedLineEndpoints.forEach((draggedLine) => {
            [draggedLine.startPosition, draggedLine.endPosition].forEach((endpointPos, idx) => {
              allExistingLineEndpoints.forEach((existingEndpoint) => {
                const du = endpointPos.u - existingEndpoint.position.u;
                const dv = endpointPos.v - existingEndpoint.position.v;
                const distance = Math.sqrt(du * du + dv * dv);
                
                if (distance < closestDistance) {
                  closestDistance = distance;
                  const endpointOffset = {
                    u: endpointPos.u - snappedPos.u,
                    v: endpointPos.v - snappedPos.v,
                  };
                  bestSnapPosition = {
                    u: existingEndpoint.position.u - endpointOffset.u,
                    v: existingEndpoint.position.v - endpointOffset.v,
                  };
                  bestTargetConnection = {
                    type: 'line',
                    blockId: existingEndpoint.blockId,
                    lineIndex: existingEndpoint.lineIndex,
                    isStart: existingEndpoint.isStart,
                  };
                }
              });
            });
          });

          if (bestSnapPosition && bestTargetConnection) {
            // Snapping aktiv!
            setDragPosition(bestSnapPosition);
            setSnapTarget(bestTargetConnection);
          } else {
            // Kein Snap - normale Grid-Position
            setDragPosition(snappedPos);
            setSnapTarget(null);
          }
        }
      } catch (error) {
        // Ignore
      }
    },
    [viewState, gridSize, getAllSockets, getAllLineEndpoints, snapEnabled]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLCanvasElement>) => {
      e.preventDefault();

      if (!draggedBlockData || !dragPosition) return;

      // Create new placed block
      const newBlock: PlacedBlock = {
        id: `block-${Date.now()}`,
        blockData: draggedBlockData,
        transform: createDefaultTransform(dragPosition),
      };

      // If snapped, create connection with block
      if (snapTarget && onBlockAddWithConnection) {
        const targetBlock = blocks.find((b) => b.id === snapTarget.blockId);
        if (!targetBlock) {
          onBlockAdd(newBlock);
          return;
        }

        // Get connection point position on target block
        let targetConnectionPos: IsoPoint | null = null;

        if (snapTarget.type === 'socket' && snapTarget.socketIndex !== undefined) {
          // Target is a socket
          const targetSockets = getTransformedSockets(
            targetBlock.blockData.elements,
            targetBlock.transform
          );
          const targetSocket = targetSockets.find((s) => s.index === snapTarget.socketIndex);
          if (targetSocket) {
            targetConnectionPos = targetSocket.position;
          }
        } else if (snapTarget.type === 'line' && snapTarget.lineIndex !== undefined) {
          // Target is a line endpoint
          const targetLines = getTransformedLineEndpoints(
            targetBlock.blockData.elements,
            targetBlock.transform
          );
          const targetLine = targetLines.find((l) => l.index === snapTarget.lineIndex);
          if (targetLine) {
            targetConnectionPos = snapTarget.isStart ? targetLine.startPosition : targetLine.endPosition;
          }
        }

        if (!targetConnectionPos) {
          onBlockAdd(newBlock);
          return;
        }

        // Find closest connection point on new block
        const newBlockSockets = getTransformedSockets(
          draggedBlockData.elements,
          newBlock.transform
        );
        const newBlockLineEndpoints = getTransformedLineEndpoints(
          draggedBlockData.elements,
          newBlock.transform
        );

        let closestDistance = Infinity;
        let closestConnection: {
          type: 'socket' | 'line';
          socketIndex?: number;
          lineIndex?: number;
          isStart?: boolean;
        } | null = null;

        // Check sockets
        newBlockSockets.forEach((socket) => {
          const du = socket.position.u - targetConnectionPos!.u;
          const dv = socket.position.v - targetConnectionPos!.v;
          const distance = Math.sqrt(du * du + dv * dv);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestConnection = {
              type: 'socket',
              socketIndex: socket.index,
            };
          }
        });

        // Check line endpoints
        newBlockLineEndpoints.forEach((line) => {
          [line.startPosition, line.endPosition].forEach((pos, idx) => {
            const du = pos.u - targetConnectionPos!.u;
            const dv = pos.v - targetConnectionPos!.v;
            const distance = Math.sqrt(du * du + dv * dv);

            if (distance < closestDistance) {
              closestDistance = distance;
              closestConnection = {
                type: 'line',
                lineIndex: line.index,
                isStart: idx === 0,
              };
            }
          });
        });

        if (closestConnection) {
          const connection: SocketConnection = {
            id: `conn-${Date.now()}`,
            sourceBlockId: snapTarget.blockId,
            targetBlockId: newBlock.id,
          };

          // Set source connection data
          if (snapTarget.type === 'socket') {
            connection.sourceSocketIndex = snapTarget.socketIndex;
          } else if (snapTarget.type === 'line') {
            connection.sourceLineIndex = snapTarget.lineIndex;
            connection.sourceIsStart = snapTarget.isStart;
          }

          // Set target connection data
          if (closestConnection.type === 'socket') {
            connection.targetSocketIndex = closestConnection.socketIndex;
          } else if (closestConnection.type === 'line') {
            connection.targetLineIndex = closestConnection.lineIndex;
            connection.targetIsStart = closestConnection.isStart;
          }

          console.log('🔌 CREATED CONNECTION:', connection.id, 'DN:', connection.dn, 'on block:', newBlock.id);
          
          // Log connection type
          const connType = 
            (connection.sourceSocketIndex !== undefined ? 'Socket' : 'Line') + 
            ' → ' + 
            (connection.targetSocketIndex !== undefined ? 'Socket' : 'Line');
          console.log('🔗 Connection type:', connType);
          
          // Use atomic operation
          onBlockAddWithConnection(newBlock, connection);
        } else {
          onBlockAdd(newBlock);
        }
      } else {
        // No snap - just add block
        console.log('📦 Adding block without connection:', newBlock);
        onBlockAdd(newBlock);
      }

      // Reset drag state
      setDraggedBlockData(null);
      setDragPosition(null);
      setSnapTarget(null);
    },
    [draggedBlockData, dragPosition, snapTarget, blocks, onBlockAdd, onBlockAddWithConnection]
  );

  const handleDragLeave = useCallback(() => {
    setDraggedBlockData(null);
    setDragPosition(null);
    setSnapTarget(null);
  }, []);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault(); // Prevent default context menu

      if (!onBlockContextMenu) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;

      const worldPos = screenToWorld({ x: screenX, y: screenY }, viewState);
      const isoPos = screenToIso(worldPos, gridSize);

      // Check if right-clicking on a block (reverse order to get topmost block)
      for (let i = blocks.length - 1; i >= 0; i--) {
        const block = blocks[i];
        
        // Check if click is within block bounds
        const transformedElements = block.blockData.elements.map((el) => {
          if (el.type === 'line') {
            return {
              type: 'line',
              start: transformPoint(el.start, block.transform),
              end: transformPoint(el.end, block.transform),
            };
          }
          return null;
        }).filter(Boolean);

        // Simple hit test: check if click is near any element of the block
        let isNearBlock = false;
        for (const el of transformedElements) {
          if (el && el.type === 'line') {
            const line = el as { type: 'line'; start: IsoPoint; end: IsoPoint };
            // Check if point is near line (within 1 grid unit)
            const minU = Math.min(line.start.u, line.end.u) - 1;
            const maxU = Math.max(line.start.u, line.end.u) + 1;
            const minV = Math.min(line.start.v, line.end.v) - 1;
            const maxV = Math.max(line.start.v, line.end.v) + 1;
            
            if (isoPos.u >= minU && isoPos.u <= maxU && isoPos.v >= minV && isoPos.v <= maxV) {
              isNearBlock = true;
              break;
            }
          }
        }

        if (isNearBlock) {
          // Call context menu with absolute screen coordinates
          onBlockContextMenu(block.id, e.clientX, e.clientY);
          return;
        }
      }
    },
    [blocks, viewState, gridSize, onBlockContextMenu]
  );

  return (
    <canvas
      ref={canvasRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onDoubleClick={handleDoubleClick}
      onWheel={handleWheel}
      onContextMenu={handleContextMenu}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
      style={{
        width: '100%',
        height: '100%',
        cursor: isPanning ? 'grabbing' : isDragging ? 'move' : activeTool === 'label' ? (labelPlacementStep === 'select-element' ? 'pointer' : 'crosshair') : hoveredTextId ? 'pointer' : 'default',
      }}
    />
  );
}